/**
 * Liveries — the paint a vehicle wears.
 *
 * ▶ **Adding one? Read `docs/authoring-liveries.md` first.** It is the whole procedure,
 *   with a copy-paste template, the two fields that are easy to get wrong (`finish` and
 *   `farColor`), the bus's 1.72× vertical stretch, and how to verify. This header is the
 *   design record; that document is the instructions.
 *
 * A livery is ARTWORK on the body's side, not a tint over the model: `getColor` on a
 * Scenegraph layer multiplies the whole mesh, so it cannot repaint a flank without taking
 * the glazing, the tyres and every placard with it (see the standing no-tint rule in
 * `transitMapGeometry.ts`). Instead each body carries a **livery panel** — its sidewall,
 * on its own material, unwrapped as a side elevation by the exporters in `mta-assets`
 * (`assign_livery_panel`). A livery paints that panel and nothing else.
 *
 * **A livery is DRAWN, not downloaded.** It is a function that fills a side-elevation
 * canvas, which is the same way this app already makes every route bullet, station badge
 * and R160 placard — so adding one is a function here rather than an asset pipeline, a
 * build step and a cache-busting rev. A future image-backed livery still fits: draw an
 * `Image` into the same canvas.
 *
 * Three things a livery must supply, because a wrap has to survive the LOD ladder:
 *   • `draw`      — the side elevation itself.
 *   • `finish`    — metallic/roughness for the panel. ⚠️ Not optional and rarely the
 *                   default: the R160's panel is bare stainless at metallic 0.95, and a
 *                   painted vinyl wrap is a DIELECTRIC. Leave it metallic and a red livery
 *                   renders as a dark mirror rather than a red train.
 *   • `farColor`  — what the vehicle wears at L2, where the whole body is one merged shell
 *                   with no UVs at all (the exporters strip them). A 12–48 px vehicle
 *                   cannot show a picture, but it can absolutely show that it is red, and
 *                   a livery that vanished at distance would look like a rendering bug.
 */

import { canvasBulletFont } from '../../ui/theme'

/** Which side of the vehicle this call is painting. See `LIVERY_CANVAS_H`. */
export type LiveryFlank = 'left' | 'right'

/**
 * The surfaces a livery may cover, and the question every new livery has to answer.
 *
 * A wrap is not the same shape on every vehicle: the classic scheme wants glazing you can
 * see through and doors left in bare steel, a full ad wrap wants the whole flank including
 * the windows, and the Minions want a face on the nose. So coverage is CHOSEN per livery
 * and per vehicle rather than being a property of the body.
 *
 * Each is a material the exporters carve out and unwrap (`claim_flank_surface` /
 * `assign_end_cap` in `mta-assets`), defaulting to the surface it was cut from — so a
 * livery that does not name one leaves it exactly as authored.
 *
 *   `sidewall`  the body's own flank. Every painted livery wants this.
 *   `doors`     the door leaves. Same plane and same unwrap as the sidewall, so `draw`
 *               already covers them — naming this only stops the artwork being sliced by
 *               a bare steel stripe every few metres.
 *   `windows`   the side glazing, as perforated vinyl. Also the sidewall's own unwrap.
 *               ⚠️ The vehicle stops reading as having windows: right for a full wrap,
 *               wrong for anything that wants to look like a working vehicle.
 *   `front`     the nose, on its OWN front-elevation canvas — needs `drawFront`.
 *   `rear`      the tail, likewise — needs `drawRear`.
 *
 * Bus and train need not agree, and mostly should not. Only the bus has `front`/`rear`
 * today: an R160's cab is glazing and a black front plate, with no skin to paint.
 */
export type LiverySurface = 'sidewall' | 'doors' | 'windows' | 'front' | 'rear'

export type LiveryPaint = {
  /**
   * Fills ONE flank's `w × h` side elevation. Called once per flank with the origin
   * already moved to that flank's island and a clip applied, so a livery is authored as a
   * single side view and never has to know the sheet is two of them stacked.
   *
   * `y = 0` is the roofline. **`x = w` is the NOSE on the right flank and the REAR on the
   * left one** — the exporters flip u on the left island (`1 - u if left else u`), and a
   * mirror cannot preserve both the reading direction and which end is which. It preserves
   * READING: an advert's text is never backwards, on either side, which is what a wrap
   * needs and what a real one does. The price is that a marking anchored to an END is at
   * opposite ends of the two flanks unless it asks.
   *
   * That is what `flank` is for. Ordinary artwork ignores it — a band, a sweep, a field of
   * repeats does not care — and anything handed (a flag's union, a face's nose) uses it.
   * `panelScale`'s `x` is in sheet space, so mirror it yourself: `flank === 'right' ? s.x(m)
   * : w - s.x(m)`.
   *
   * `body` is the same idea one level up: a fleet is not one body. The subway slot is
   * called once for the R160-A and once for the R160-B, which are different vehicles with
   * different flanks — a cab car has an operator's window where the other has a passenger
   * bay — so a scheme may draw them differently, and `mta` does. A livery that treats the
   * fleet as uniform simply ignores it, as `minion` does.
   */
  draw: (
    ctx: CanvasRenderingContext2D,
    w: number,
    h: number,
    flank: LiveryFlank,
    body: LiveryBody,
  ) => void
  /**
   * Which surfaces this paint covers on this vehicle. Omitted means `['sidewall']`, which
   * is what every livery did before coverage was selectable.
   *
   * ⚠️ `doors` and `windows` need nothing from you beyond naming them — they share the
   * sidewall's plane AND its unwrap, so `draw` already reaches them and the artwork runs
   * across a door leaf unbroken. `front` and `rear` are different planes and need their
   * own picture; naming one without supplying its `draw*` leaves that surface as authored.
   */
  surfaces?: readonly LiverySurface[]
  /**
   * The nose, in front elevation: `u` runs across the bus from its RIGHT to its left, `v`
   * up from the bottom of the cap. Only called when `surfaces` names `front`.
   *
   * ⚠️ The windscreen, headlights, trim and destination sign are NOT part of this surface
   * — they are their own materials and stay real. So paint the whole rectangle and let
   * them mask it: a goggle rim drawn over the windscreen shows only where the skin is,
   * which is exactly how a bus wearing goggles should look.
   */
  drawFront?: (ctx: CanvasRenderingContext2D, w: number, h: number) => void
  /** The tail, in rear elevation, same convention. Only called when `surfaces` names `rear`. */
  drawRear?: (ctx: CanvasRenderingContext2D, w: number, h: number) => void
  finish: { metallic: number; roughness: number }
  /**
   * The ROOF and everything else left on the body's blockout material, as one flat colour.
   *
   * ⚠️ This is not a detail, because **this map looks straight down.** Nothing sets a pitch,
   * so the surface a vehicle presents to the camera by default is its roof — and the roof
   * has no usable texture coordinates (the exporters only unwrap the flanks and the caps), so
   * it can be coloured but not drawn on. Skip it and a liveried bus is a painted flank under
   * a blockout-grey lid: the as-built bus got away with the grey only because `getColor`
   * multiplied the route colour over it, which is exactly what a liveried bus drops.
   *
   * LINEAR 0..1, like `farColor` — `baseColorFactor` is defined in linear space while the
   * artwork beside it is an sRGB canvas that the shader decodes
   * (`SRGBtoLINEAR(texture(...)) * baseColorFactor` in luma's PBR fragment shader). Write it
   * with `linearFromHex` so the roof and the paint it abuts quote one hex.
   *
   * Optional, and the subway liveries deliberately leave it unset: an R160's roof is bare
   * stainless in reality, and every subway livery here is a flank wrap on a steel car.
   */
  shellColor?: [number, number, number]
  /**
   * Linear-ish RGB 0..1, straight into `baseColorFactor` on the far SHELL.
   *
   * It repaints the body and nothing else: the exporters split L2 into that shell and a
   * `*_FarDark` carrying the running gear and glazing — the R160's trucks and wheels, the
   * Xcelsior's tyres and hubs — precisely so a livery stops at the same surfaces here that
   * it stops at up close. Before the split, one merged shell meant a green bus got green
   * tyres. Nothing in this table can reach `FarDark`, which is the point of it.
   */
  farColor: [number, number, number]
}

export type Livery = {
  label: string
  /** One line, shown under the label in the picker. */
  note: string
  /**
   * What each body wears under this theme, or `null` for **"not offered on this body"** —
   * that vehicle's picker leaves the row out entirely and the fleet keeps the paint it
   * already had.
   *
   * `classic` is the entry that forced the distinction, and the reason is a fact about the
   * agency rather than about this code: the white body with the dark window band is a BUS
   * scheme, and there has never been a classic MTA *subway* livery beside it — R160s are
   * unpainted stainless. Drawing one anyway invented a piece of the fleet's history and
   * then offered it to riders as though it were real.
   *
   * ⚠️ `default` is `null` on both and is nonetheless a real design rather than an absence,
   * so its null says nothing about who is offered it — `AS_BUILT_OFFERED` does, and today
   * that is the trains alone. Either way a picker asks `LIVERY_IDS_FOR`, never a bare null
   * check, and the two lists are deliberately different lengths.
   */
  subway: LiveryPaint | null
  bus: LiveryPaint | null
}

/**
 * The panel sheet. Wide and short because that is the shape of a vehicle's side: the
 * R160's panel measures 17.73 m × 2.25 m and the Xcelsior's 12.27 m × 2.64 m, both far
 * wider than tall. One sheet serves both bodies — each stretches it over its own panel —
 * so a livery that wants to line up with a real feature should key off fractions of
 * `w`/`h` rather than pixels.
 *
 * ⚠️ The SHEET is `LIVERY_CANVAS_W × LIVERY_CANVAS_H`, but a livery's `draw` is handed
 * `LIVERY_CANVAS_W × LIVERY_FLANK_H` — half of it. The exporters unwrap each flank onto
 * its own island, stacked (the vehicle's physical LEFT is the top half), because a single
 * shared island can only carry markings symmetric about the vehicle's centre and the
 * R160-A's are not. The runtime paints both islands for you.
 *
 * Horizontal density is ~58 px/m on the R160, against ~35 px/m on screen at the map's
 * maximum zoom — so a marking baked into this sheet has headroom rather than softening.
 */
export const LIVERY_CANVAS_W = 1024
export const LIVERY_FLANK_H = 128
export const LIVERY_CANVAS_H = LIVERY_FLANK_H * 2

/**
 * The end-cap sheet, one per cap, and square because the caps nearly are: the Xcelsior's
 * nose measures 2.55 m across by 2.93 m tall and its tail 2.57 × 2.53. At 256 px that is
 * ~100 px/m, denser than the flank's 84, which it can afford — a cap is a fraction of the
 * flank's area and only two vehicles' worth of it is ever drawn.
 *
 * ⚠️ A cap CANNOT share the flank sheet. It is a different plane, so a picture unwrapped
 * in side elevation would arrive on the nose stretched along an axis that does not exist
 * there. Its own canvas is the cheap fix; the cost is one more texture per livery that
 * asks for one, and none at all for the liveries that do not.
 */
export const LIVERY_CAP_W = 256
export const LIVERY_CAP_H = 256

const SIDEWALL_ONLY: readonly LiverySurface[] = ['sidewall']

/** What this paint covers — the table's default is the sidewall alone. */
export function liverySurfaces(paint: LiveryPaint): readonly LiverySurface[] {
  return paint.surfaces ?? SIDEWALL_ONLY
}

/** MTA's own colours, so a livery that quotes the agency quotes it exactly. */
const MTA_BLUE = '#0039A6'
const MTA_YELLOW = '#FFCC00'
const BUS_WHITE = '#EDEFF2'
const STAINLESS = '#C9CDD2'

/**
 * What `default` looks like, for the one consumer that has to SHOW it: the picker's swatch.
 *
 * `default` carries no paint on purpose — `null` is what buys the plain model URL and skips
 * the whole patched-glTF path — so there is no `draw` to ask what as-built looks like, and
 * anything drawing a preview would otherwise have to invent a grey. These are the two body
 * colours the exporters give the flanks, quoted from the constants above so the swatch and
 * the artwork beside it cannot drift apart.
 *
 * ⚠️ Only the subway entry reaches a picker now (`AS_BUILT_OFFERED`). The bus's is kept
 * because it is the honest answer to "what colour is an undressed Xcelsior", which the
 * benchmark fleet draws and any future preview of it would need; it is not a scheme on offer.
 */
export const AS_BUILT_FLANK: Record<LiveryVehicle, string> = {
  subway: STAINLESS,
  bus: BUS_WHITE,
}

/**
 * The window mask of the classic scheme. Not black: a pure black band beside real
 * glazing reads as a hole, because the glass itself is a lit dielectric and never gets
 * this dark. This is the surround that makes a row of separate windows read as one dark
 * ribbon, which is the whole effect the classic livery is after.
 */
const WINDOW_MASK = '#16191D'

/** The flag's own colours — Old Glory Red and Blue, not the MTA blue beside them. */
const FLAG_RED = '#B31942'
const FLAG_BLUE = '#0A3161'
const FLAG_WHITE = '#FFFFFF'

/**
 * The wrap's third colour, which MTA does not publish, sampled off the two flank photographs
 * `reference/BUS-XCEL-40/XE40_photo_mta_side_offside.png` (overcast) and
 * `..._side_doorside.png` (full sun) rather than guessed. The medians of the cyan family in
 * the pinstripe fan and the lower sweep come back #0BA6D2 and #079ACA in the overcast frame
 * and #02B2FD in the sunlit one; this sits between them.
 *
 * ⚠️ **The wrap has three colours and white is not one of them** — blue body, agency gold and
 * this cyan. The white on a real bus is lettering only ("clean-energy bus", "HYBRIDBUS", the
 * fleet number, the roundel). An earlier reading of the low-resolution front-three-quarter
 * photo put a white band in the middle of the flank graphic; both flank photographs show that
 * band is cyan, and the pale value beside it is `WRAP_GOLD_PALE`, not white.
 */
const WRAP_CYAN = '#12A7D8'

/**
 * The shoulder arc's gold, which is a genuinely lighter tint than the gold on the flank below
 * it — not the same paint in different light. `XE40_photo_mta_side_offside.png` samples
 * #DABC6C on the shoulder against #D3A427 on the lower sweep in the SAME frame, and the
 * sunlit frame keeps the split (a cream shoulder over a saturated #FDD91C band). So the wrap
 * carries gold at two values, and flattening them loses the shoulder against the roof.
 */
const WRAP_GOLD_PALE = '#E6D19A'

/**
 * sRGB hex → the LINEAR triple `baseColorFactor` actually wants, for `shellColor` and
 * `farColor`.
 *
 * Not a formality: the factor is linear while every hex in this file is sRGB, and the two are
 * furthest apart in exactly the dark half MTA blue lives in — its 0x39 green is 0.224 as sRGB
 * and 0.041 linear, a factor of five. Hand-picking a flat colour to match painted artwork is
 * what lets a roof drift away from the flank it meets, and that shows up as a seam along the
 * roof line on a map that looks down at the roof.
 */
export function linearFromHex(hex: string): [number, number, number] {
  const n = Number.parseInt(hex.slice(1), 16)
  return ([16, 8, 0] as const).map((shift) => {
    const c = ((n >> shift) & 0xff) / 255
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  }) as [number, number, number]
}

/**
 * Body metres → sheet x for a marking that is anchored to an END of the vehicle.
 *
 * ⚠️ Any such marking MUST go through this, because `x = w` is the nose on the right flank and
 * the REAR on the left one (see `draw`). A yellow tail drawn straight through `s.x` is a
 * yellow NOSE on the far side of the bus — the single easiest thing to get wrong on this
 * sheet, and invisible until you circle the vehicle. `flagDecal` has always done this inline;
 * this is the same two lines, named, so the liveries that need it read as asking for it.
 */
function flankX(s: Scale, w: number, flank: LiveryFlank): (metres: number) => number {
  return (metres) => (flank === 'right' ? s.x(metres) : w - s.x(metres))
}

/**
 * The classic scheme's belt, in metres above the road — and the reason it is a constant
 * rather than a number written inside one `draw`.
 *
 * The belt crosses the bus's FRONT, which is a different surface on a different unwrap, so
 * the flank and the nose are painted by two functions. They meet at the front corner, where
 * a couple of centimetres of drift reads as a band that breaks where the bus turns, so the
 * height is ONE decision that both read.
 *
 * ⚠️ Both figures are MEASURED off the reference elevation, not chosen — see
 * `CLASSIC_REFERENCE` for the calibration. The belt was 1.06–1.34 (0.28 m) and flush under
 * the window band; the drawing has it 0.245 m and a hand's breadth lower, with white
 * showing between it and the glass. Those two together are most of why the earlier belt
 * read as a heavy stripe: a band that touches the black one above it is seen as part of it.
 */
const BUS_BELT_Z = [0.955, 1.2] as const

/**
 * The classic scheme's window band on the bus, [bottom, top] in metres, and its [rear,
 * nose] run along the body. Also measured (`CLASSIC_REFERENCE`).
 *
 * It is DELIBERATELY wider than the glazing it covers (1.362–2.3269): the drawing's dark
 * ribbon is the glass *plus* the black frame around it, ~0.09 m proud at the header and
 * ~0.10 m below the sill, and that frame is what makes a row of separate openings read as
 * one band. Bracketing the glass exactly — which is what the earlier [1.34, 2.36] did —
 * paints the panes and leaves the surround white, so the band arrived on the body cut into
 * pieces by its own pillars.
 */
const BUS_MASK_Z = [1.25, 2.41] as const
const BUS_MASK_X = [-5.1, 6.06] as const

// ── CLASSIC_REFERENCE ─────────────────────────────────────────────────────────────────
// The reference the classic bus is drawn from: a four-view orthographic elevation of an MTA
// New Flyer Xcelsior (both flanks, front and rear), which is the artwork every figure in
// `classic.bus` is converted out of.
//
// **It calibrates at 79.8 px/m vertically**, and that is not an assumed scale — it is the
// one that puts the drawing's own body edges on this model's measured panel: the white body
// bottoms out at the row `PANEL.bus.z0` (0.3048) predicts and the roof shoulder at the row
// `z1` (2.9466) predicts, two points 2.64 m apart agreeing within 3 px. Along the body the
// drawing's belt line runs 935 px for the panel's 12.2676 m, which is the mapping every x
// in the classic bus came through.
//
// ⚠️ **The drawing's side view is the bus's LEFT flank** — stand outside the left side and
// the nose is on your left, which is the view it shows. That is what fixes the reading order
// of the wordmark (see `mtaWordmark`), and it is worth stating because getting it backwards
// mirrors every handed marking on the vehicle.
//
// Two things it shows that are deliberately NOT drawn:
//  • the belt stops ~1.4 m short of the tail, where the real bus's engine access door
//    begins. This body has no such door, so a belt that simply stopped mid-flank would read
//    as a gap in the paint rather than as a panel joint.
//  • the band's rear edge is raked, following a rear side window that is taller at the top.
//    The model's own glazing is not that shape, so a raked mask would leave a wedge of that
//    window painted white — worse than the square end it has.

/**
 * The bus's side glazing, [sill, header] in metres — measured off the L0 GLB's
 * `MAT_BLOCKOUT_WINDOW_FRAME` and `MAT_BLOCKOUT_GLASS` primitives, not off a drawing.
 *
 * It is what divides the flank into the three regions any bus livery has to reckon with: the
 * 0.62 m of continuous wall above the header (up to the panel's own top edge at 2.947), the
 * band itself — where only the pillars between the openings are this livery's surface — and
 * the 1.06 m of wall below the sill.
 */
const BUS_GLAZING_Z = [1.362, 2.3269] as const

/**
 * The FRONT door's two panes, as polygons in the bus's own metres — `x` along the body,
 * `z` above the road. Measured off the shipped L0 GLB's `MAT_LIVERY_GLASS` primitive (the
 * two tall apertures on the door flank, x 4.8388–5.1818 and 5.3962–5.7701 against the
 * passenger bays' 1.39–2.30), not off the line drawing they were traced from.
 *
 * ⚠️ **Door side only.** The Xcelsior is not symmetric: `DOORS_RIGHT` in the blockout puts
 * both doors on the vehicle's RIGHT, and the far flank carries the DRIVER'S WINDOW at this
 * station (4.868–5.848 against the door's 4.747–5.893). So these are drawn on the `right`
 * island alone — painted on both, the second copy lands on the driver's own glass.
 *
 * (This used to say "the driver's window and a blank panel". The blank was real and was a
 * model defect, not a feature of the vehicle: a passenger bay had been deleted from
 * `WINDOWS_LEFT` in the blockout. It is back as of `?rev=9`, so the far flank is glazed the
 * whole way and there is no blank anywhere on it.)
 *
 * The forward pane follows the door's raked forward jamb — flat to z 1.727, then back to
 * 5.724 at the header — so it is the polygon rather than its bounding box: squared off, it
 * spills 4.6 cm onto the jamb it is meant to stop at.
 */
const BUS_FRONT_DOOR_PANES: ReadonlyArray<ReadonlyArray<readonly [number, number]>> = [
  [[4.8388, 0.5573], [5.1818, 0.5573], [5.1818, 2.2356], [4.8388, 2.2356]],
  [[5.3962, 0.5573], [5.7701, 0.5573], [5.7701, 1.7272], [5.7239, 2.2356], [5.3962, 2.2356]],
]

/** The CENTRE door's two panes, measured the same way as the front door's above. */
const BUS_CENTRE_DOOR_PANES: ReadonlyArray<ReadonlyArray<readonly [number, number]>> = [
  [[-1.5078, 0.5791], [-1.1676, 0.5791], [-1.1676, 2.2172], [-1.5078, 2.2172]],
  [[-0.9555, 0.5791], [-0.6279, 0.5791], [-0.6279, 2.2172], [-0.9555, 2.2172]],
]

/**
 * The PASSENGER BAY glazing, per flank — the apertures that are not door leaves.
 *
 * Measured off the shipped L0 GLB the same way the two door-pane sets above were: decode
 * `MAT_LIVERY_GLASS`, split the faces by which side of the bus they sit on, cluster along the
 * body. The method checks out because it reproduces both door sets to within 0.3 mm of the
 * constants already here, which were measured independently.
 *
 * ⚠️ **The two flanks are NOT the same, and this is the one place that matters.** The Xcelsior
 * carries both doors on its right, so the door side has five bays and the offside SEVEN — six
 * passenger bays plus the DRIVER's window at 4.868–5.845, which on the right flank is the front
 * door. Anything placed against "the window at this station" has to know which side it is on.
 *
 * ⚠️ The offside was SIX until `?rev=9`, and the missing one was a model defect rather than a
 * fact about the vehicle: a `FORWARD` bay had been deleted from `WINDOWS_LEFT` in the blockout,
 * leaving 1.673 m of dead wall behind the driver's window in a flank whose every other bay is
 * on a ~53.5 in pitch. It is the entry at 3.308–4.645 below. The lesson for this table is that
 * it is DERIVED — decode the shipped GLB after any re-export rather than trusting the list,
 * because a livery cannot tell a missing aperture from a wall and will happily wrap one.
 *
 * The rear bay is a five-sided polygon rather than a box on both flanks: its rear edge starts
 * at 1.807 where its forward edge starts at 1.415, so the sill rakes up toward the tail. Boxed
 * off at the lower figure it would paint 0.4 m of SIDEWALL glass-dark under the raked corner —
 * a dark notch in the wrap, on the quarter where the gold stripe fan is busiest.
 *
 * Each bay is ONE box spanning sill to header, though the mesh splits every one of them into an
 * upper and a lower light either side of a transom. The bar between is z 1.991–2.013 — 22 mm,
 * under half a sheet pixel — so tracking it would double the table to describe something that
 * cannot be drawn.
 */
const BUS_BAY_PANES: Record<
  LiveryFlank, ReadonlyArray<ReadonlyArray<readonly [number, number]>>
> = {
  left: [
    [[-4.9, 1.8073], [-3.5405, 1.4151], [-3.5405, 2.2927], [-4.9, 2.2927]],
    [[-3.2053, 1.4617], [-1.8503, 1.4617], [-1.8503, 2.2999], [-3.2053, 2.2999]],
    [[-1.548, 1.3968], [-0.1753, 1.3968], [-0.1753, 2.3038], [-1.548, 2.3038]],
    [[0.1212, 1.3896], [1.4814, 1.3896], [1.4814, 2.2874], [0.1212, 2.2874]],
    [[1.7903, 1.385], [3.1506, 1.385], [3.1506, 2.2861], [1.7903, 2.2861]],
    // The bay restored at `?rev=9`. Its z is its neighbour's to the digit — the blockout
    // took `FRONT_BAY`'s band verbatim rather than re-tracing one — which is why the two
    // entries above and here are the only pair in this table that agree exactly.
    [[3.308, 1.385], [4.6446, 1.385], [4.6446, 2.2861], [3.308, 2.2861]],
    [[4.8683, 1.4269], [5.8448, 1.4269], [5.8448, 2.2979], [4.8683, 2.2979]],
  ],
  right: [
    [[-4.9, 1.8073], [-3.5405, 1.4151], [-3.5405, 2.2927], [-4.9, 2.2927]],
    [[-3.3319, 1.4151], [-1.7578, 1.4151], [-1.7578, 2.2966], [-3.3319, 2.2966]],
    [[-0.3983, 1.4191], [1.5988, 1.4191], [1.5988, 2.2927], [-0.3983, 2.2927]],
    [[1.8618, 1.4151], [3.3257, 1.4151], [3.3257, 2.2966], [1.8618, 2.2966]],
    [[3.5218, 1.4151], [4.6243, 1.4151], [4.6243, 2.3032], [3.5218, 2.3032]],
  ],
}

/** The rear bay, which is where the flag goes — see the `mta` bus livery. */
const BUS_REAR_BAY_X = [-4.9, -3.5405] as const

/**
 * Both door leaves as [rear, nose] along the body, and the height they span — `DOORS_RIGHT`
 * in the blockout, converted (centre door x −63.42..−21.22 in, front 186.89..232.00, both
 * z 12.54..94.53).
 *
 * ⚠️ **The leaf runs from 0.32 m, a metre below the glazing band.** That is the fact behind
 * every "the doors are painted" complaint: the belt crosses the flank at 0.955–1.20, which
 * on the real bus is body panel BELOW the door and on this model is door leaf. So a scheme
 * that claims `doors` gets its belt — and anything else it draws down there — running
 * across the leaves.
 */
const BUS_DOOR_BAYS: ReadonlyArray<readonly [number, number]> = [
  [-1.6111, -0.539],
  [4.7469, 5.8928],
]
const BUS_DOOR_LEAF_Z = [0.3185, 2.401] as const

/**
 * `MAT_LIVERY_GLASS`'s own authored base colour, back in sRGB — the paint that hands a
 * claimed pane BACK to reading as glass. (`export_bus.py` builds it at linear 0.05/0.09/
 * 0.12, which is this hex through `linearFromHex`.)
 *
 * It exists because coverage is declared per MATERIAL and the glazing is one material: a
 * wrap that claims `windows` claims every pane on the flank, and a pane it should not cover
 * cannot be un-claimed on its own. Painting it its own colour is the way out — exact in base
 * colour, not in FINISH, since a claimed surface takes the livery's `finish` and so reads
 * matte where untouched glazing is 0.10 gloss. That difference is the price of the
 * exception, and it is invisible at the size a bus draws on this map.
 */
const BUS_GLASS = '#3F5561'

/**
 * The Xcelsior's end caps, in the vehicle's own metres — `y` across the bus (+y is its
 * LEFT), `z` above the road. Measured off the L0 GLB's `MAT_LIVERY_FRONT` and
 * `MAT_REAR_CAP` primitives, which is exactly the extent `assign_end_cap` unwraps each cap
 * over, so a marking written here in metres lands where it was measured.
 */
const BUS_CAPS = {
  front: { y0: -1.2769, y1: 1.277, z0: 0.1219, z1: 3.048 },
  rear: { y0: -1.2833, y1: 1.2833, z0: 0.4216, z1: 2.9514 },
} as const

/**
 * An end cap's metres → its own sheet, the cap-elevation counterpart of `panelScale`.
 *
 * `x = 0` is the bus's own RIGHT (−y), which is what `assign_end_cap` puts at u = 0 so the
 * nose reads left-to-right for someone standing in front of it. `y = 0` is the top of the
 * cap. A cap sheet is nearly square (2.55 × 2.93 m on 256 × 256), so the flank's 1.72×
 * stretch is only 1.14 here — but `dx`/`dy` still exist so a circle is a circle.
 *
 * ⚠️ That convention is right for the NOSE and backwards for the TAIL, which is what
 * `mirror` is for. Both caps put u = 0 at the bus's own right, but the two are looked at
 * from opposite ends: an observer in FRONT faces −x, so the bus's right is on their left
 * and the sheet reads left-to-right; an observer BEHIND faces +x, so the bus's right is on
 * their RIGHT and the same sheet reads back-to-front. `mirror` measures from the cap's
 * other edge, and the caller flips the canvas to match — the two cancel for position and
 * compound for glyphs, which is exactly what a legible rear wordmark needs. See `drawRear`.
 */
function capScale(
  w: number, h: number,
  cap: { y0: number; y1: number; z0: number; z1: number },
  mirror = false,
) {
  const spanY = cap.y1 - cap.y0
  const spanZ = cap.z1 - cap.z0
  return {
    x: (m: number) => ((mirror ? cap.y1 - m : m - cap.y0) / spanY) * w,
    y: (m: number) => ((cap.z1 - m) / spanZ) * h,
    dx: (m: number) => (m / spanY) * w,
    dy: (m: number) => (m / spanZ) * h,
  }
}

// ── The R160's own measurements ───────────────────────────────────────────────────────
// The three features a subway livery has to line up with, all in the car's own metres and
// all read off the mesh or the generator rather than off a photograph. They are here rather
// than imported from `r160Model.ts` for the same reason `BUS_GLAZING_Z` is: that module
// owns PLACARDS — quads floated in front of the body — and this one owns the skin under
// them. Neither should have to load the other to know where the paint goes.

/**
 * The belt moulding, [bottom, top] in metres above the rail — the raised rib that runs the
 * length of the car under the windows, at z 77.5–79.0" in the generator (the band every
 * side decal in `r160Model.ts` is banded to clear).
 *
 * It is what "about the middle" resolves to. The panel spans 0.9444–3.1904, so its own
 * midpoint is 2.067 and the moulding sits 6 cm under it — near enough that the eye reads
 * the two as the same line, and unlike the midpoint it is a feature the body actually has.
 * A band that stops at a rib reads as paint masked off at a body line; a band that stops
 * 6 cm above one reads as paint that missed.
 */
const R160_BELT_Z = [1.9685, 2.0066] as const

/**
 * The operator's side window on the A car: `x` = [aft jamb, forward jamb], `z` = [sill,
 * header], in metres from the car's centre. This is the **aperture in the livery panel**,
 * not the glass primitive's bounds — the two differ, and only one of them is the edge paint
 * actually stops at.
 *
 * Measured off `SUB-60-R160-A_L0.glb` by point-in-triangle over the `R160_Livery` faces on
 * the +z flank, which is the only method that answers the question: a gap in the distinct-x
 * list proves nothing, because a flat panel is low-poly and carries no vertices across its
 * own interior. The wall either side of it came out of the same sweep, and the whole cab
 * end reads, forward to aft:
 *
 *   8.6210 … 8.1510   wall forward of the window (0.470 m)
 *   8.1510 … 7.7000   THIS APERTURE (0.451 m), with a mullion at z 2.3640…2.4420
 *   7.7000 … 6.5870   the cab pillar, running into the first door's forward stile (1.113 m)
 *   6.5870 … 6.1520   the first door's forward pane
 *
 * ⚠️ The aperture is a HOLE. Paint written inside it is not clipped, dimmed or drawn behind
 * the glass — there is no surface, so it does not exist, and the flash crossing the window
 * shows above the header and below the sill and nowhere in between. That is the whole reason
 * the flash can be anchored to the window's own centre: the anchor itself is invisible, and
 * what the eye gets is a diagonal passing behind the glazing.
 *
 * The sill at 2.0070 is the belt moulding's own top (`R160_BELT_Z`, 2.0066) to within 0.4 mm.
 * The band's floor and the window's sill are one line on this body, which is not a
 * coincidence worth relying on but is worth knowing before moving either.
 *
 * ⚠️ Distances from the CENTRE, unsigned. The A car is modelled with its cab at −x
 * (`R160_CAB_FORWARD_YAW`), so everything here reaches the sheet negated — see
 * `r160CabStripes`. Writing them positive is what lets them be compared against the
 * generator's own table, which is likewise unsigned.
 */
const R160_A_CAB_WINDOW = {
  x: [7.7000, 8.1510],
  z: [2.0070, 2.9360],
} as const

/**
 * The wall the flash's foot lands on: forward of the operator's window and aft of the cab
 * cap, [aft, forward] unsigned. From the same sweep as `R160_A_CAB_WINDOW`.
 *
 * It is here to be a BOUND, not an anchor — nothing is pinned to it. The foot is placed by
 * the window, and this is what says the placement had room: 8.6210 is where the panel stops,
 * and a foot past it is paint on nothing.
 */
const R160_A_CAB_FRONT_WALL = [8.1510, 8.6210] as const

/**
 * The aft bound on the flash, unsigned: where the first passenger door's forward pane opens.
 * The door LEAF is a livery surface (`R160_LiveryDoor`, and it rides the panel's own unwrap —
 * decoding its UVs gives u 0.0784…0.9217, which is the panel's mapping mirrored on the far
 * flank), so a marking may cross the door's stiles. Its two PANES are apertures like any
 * other window, so a marking may not.
 */
const R160_A_FIRST_DOOR_PANE_X = 6.5870

/**
 * The A car's MID-CAR pillar, [forward jamb, aft jamb] in **signed** metres — the wall
 * between the second door and the middle window, and where the blue band's forward edge
 * ends.
 *
 * ⚠️ It exists because the car's centre is a WINDOW. Decoding the A car's L0 flank
 * (`R160_Glass` and `R160_LiveryDoor`, +z side) gives doors at −6.690…−5.420,
 * −2.380…−1.110, 1.906…3.176, 6.204…7.474 and passenger glass at −4.557…−3.315,
 * −0.256…1.004, 4.039…5.282 — so x = 0 is 0.256 m INSIDE the middle window. A band that
 * terminated at the car's centre put its edge on glass, which is not this livery's surface:
 * the edge was drawn and simply was not there. This pillar is the nearest solid wall to the
 * centre (midpoint −0.683) and, being between a door and a window, is a body line the edge
 * can honestly break on.
 *
 * Signed, unlike `R160_A_CAB_WINDOW`, because a marking near the centre has no "distance
 * from the end" to be measured as — the sign IS the position.
 */
const R160_A_MID_PILLAR_X = [-1.11, -0.256] as const

/**
 * The rake shared by both of the A car's diagonals, as horizontal metres per metre of
 * HEIGHT — an angle rather than a length, which is the only form that can serve two
 * diagonals of different heights.
 *
 * The cab flash crosses the whole 2.246 m panel and the band's edge only the 1.184 m above
 * the moulding; written as two lengths they would be two different angles, and a livery
 * whose two slants disagree by a few degrees reads as a mistake rather than as a motif.
 *
 * ⚠️ **Chosen, not measured — this is a design call over the photograph.** 0.0846 was the
 * figure read off car 8540's operator-window surround, and it drew a diagonal 4.8° off
 * vertical: at map scale that is a vertical stripe with a rounding error on it. 0.30 is
 * 16.7°, three and a half times the lean, and it is the angle the scheme is now drawn at on
 * the user's direction. The photograph is still what the COLOURS and their order come from.
 *
 * ⚠️ **The bound is the mid-car pillar, not the cab.** The band's edge spends this over its
 * own 1.184 m — 0.355 m at 0.30 — and it has to land inside `R160_A_MID_PILLAR_X`, which is
 * 0.854 m wide: centred, that caps the slope near 0.7 before the edge reaches glazing at one
 * end or a doorway at the other, and 0.30 leaves ~0.25 m of margin at both. The flash is no
 * longer the tighter of the two, because it is no longer boxed into a pillar — it crosses
 * the operator's window (see `r160CabStripes`), so its lean is spent on wall it has plenty
 * of. That is a reversal of how these two used to constrain each other and the reason the
 * angle could move at all.
 */
const R160_SLANT_SLOPE = 0.30

/**
 * The cab-end flash, from the user's platform photograph of car 8540. Sampled off the frame
 * rather than quoted from the agency, which does not publish them: the gold is a warm amber
 * nearer old gold than the #FFCC00 of a bus tail, and the white is knocked back off pure so
 * it reads as a decal on steel rather than as a hole.
 */
const R160_STRIPE_GOLD = '#DFA83E'
const R160_STRIPE_WHITE = '#F2F4F6'

/**
 * The livery panel of each body, in the vehicle's own metres: `x` along it from the
 * centre (+x forward), `z` above the rail or the road. Measured off the exported L0 GLBs
 * — the `R160_Livery` / `MAT_LIVERY` primitives' own position bounds — and printed by
 * both exporters on every run as `LIVERY_PANEL x0=… x1=… z0=… z1=…`, so these are read,
 * not chosen. The unwrap covers exactly these extents, which is what lets a marking be
 * written where it is on the real vehicle and land there on the sheet.
 *
 * ⚠️ The panels are nothing like each other across FLEETS: a bus sidewall starts 0.30 m
 * off the road and a train's 0.94 m above the rail, and their window bands sit at
 * different fractions of their own height. One set of numbers cannot dress both.
 *
 * ⚠️ The two R160 bodies are keyed SEPARATELY even though their numbers agree to the
 * digit, and that is not redundancy. The panel is measured per body, and the fact that
 * the A car's panel spans the same extents as the B car's is a measurement — the two
 * bodies do not otherwise agree on anything a decal cares about (see `r160APillarX`,
 * `r160SignBay`). What actually differs is the ARTWORK: the `mta` scheme is a different
 * drawing on a cab car, so `draw` is handed the body rather than the fleet.
 */
const PANEL = {
  'r160-a': { x0: -8.8625, x1: 8.8625, z0: 0.9444, z1: 3.1904 },
  'r160-b': { x0: -8.8625, x1: 8.8625, z0: 0.9444, z1: 3.1904 },
  xcel40: { x0: -6.1018, x1: 6.1658, z0: 0.3048, z1: 2.9466 },
} as const

/**
 * WHICH BODY is being painted, as against `LiveryVehicle`, which is which FLEET a rider
 * picked paint for. The distinction exists because a consist is not one body: an R160 set
 * is A-B-B-A, and a cab car's flank is not a non-cab car's.
 *
 * `liveryScenegraph` already patches a separate glTF per model URL, so the body is known
 * at exactly the point the canvas is drawn; this is what carries it into `draw`.
 */
export type LiveryBody = keyof typeof PANEL

/**
 * The panel's true side-elevation aspect (width ÷ height), which is NOT the sheet's.
 *
 * One 1024 × 128 island serves both bodies (8:1), and each body stretches it over its own
 * panel — 7.89:1 on the R160, 4.64:1 on the Xcelsior. On the vehicle that stretch is the
 * renderer's business and nobody has to think about it, but anything showing the sheet as a
 * PICTURE has to undo it, or a bus's paint is displayed a third too long and the artwork
 * reads at proportions the bus never wears. `liveryPreview.ts` is the one caller.
 */
export function liveryPanelAspect(body: LiveryBody): number {
  const { x0, x1, z0, z1 } = PANEL[body]
  return (x1 - x0) / (z1 - z0)
}

/**
 * The panel's extents in metres, for framing a CAMERA on the body it wraps.
 *
 * A flank panel is not a bounding box, but it is within a few percent of one in the two
 * directions that matter: it runs 97% of both bodies' length (17.73 m of the R160's 18.35,
 * 12.27 m of the Xcelsior's 12.68) and its top edge is the roofline. So a preview can frame
 * the vehicle off these plus a margin, instead of a second set of hand-copied dimensions —
 * and there is no third place for the numbers to drift. See `liveryPreview.ts`.
 */
export function liveryPanelBox(body: LiveryBody): { x0: number; x1: number; z0: number; z1: number } {
  return { ...PANEL[body] }
}

type PanelBody = LiveryBody

/** Position x (metres, +x forward) → the sheet's x fraction, measured from the REAR. */
function bandX(body: PanelBody, x: number): number {
  const { x0, x1 } = PANEL[body]
  return (x - x0) / (x1 - x0)
}

/** Height z (metres) → the sheet's y fraction, measured DOWN from the roofline. */
function bandY(body: PanelBody, z: number): number {
  const { z0, z1 } = PANEL[body]
  return (z1 - z) / (z1 - z0)
}

/**
 * A sine band the length of the body, thick, in one flat colour: the "swept band" idea, as
 * a fraction of the sheet rather than in body metres.
 *
 * ⚠️ It is deliberately NOT how the agency scheme is drawn any more. The `mta` livery used
 * to be this wave in blue over gold on both vehicles, which was an invention on the bus and
 * an invention on the train; both now paint what their references show, in the metres those
 * were measured at. What is left here is the Minion's roofline and skirt shading, where a
 * soft wash across a cartoon flank is exactly what a fraction-of-the-sheet curve is for.
 */
function sweep(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  color: string,
  {
    centre = 0.62,
    amplitude = 0.16,
    thickness = 0.3,
    phase = 0,
    cycles = 1.15,
  }: { centre?: number; amplitude?: number; thickness?: number; phase?: number; cycles?: number } = {},
) {
  ctx.beginPath()
  const at = (x: number) => centre * h + Math.sin(phase + (x / w) * cycles * Math.PI * 2) * amplitude * h
  for (let x = 0; x <= w; x += 4) ctx.lineTo(x, at(x))
  for (let x = w; x >= 0; x -= 4) ctx.lineTo(x, at(x) + thickness * h)
  ctx.closePath()
  ctx.fillStyle = color
  ctx.fill()
}

// ── The Minions ───────────────────────────────────────────────────────────────────────
// The one livery that paints a CHARACTER rather than a scheme, and the one that made the
// case for choosing surfaces at all: a minion needs a FACE, and a face belongs on the nose.
//
// It has been three things, and the last two are worth knowing apart. First a whole-model
// `getColor` multiply — the exact thing the no-tint rule in `transitMapGeometry.ts` warns
// about, which painted the glazing, the tyres and the trucks yellow along with the body and
// gave the train no minion at all. Then paint on the sidewall plus six overlay MESHES on
// the bus's front cap, because no front surface carried texture coordinates. Now it is
// paint the whole way: `assign_end_cap` in `export_bus.py` carves the cap out and unwraps
// it, so the face is `drawFront` below and those six SimpleMeshLayers per bus are gone.
//
// The palette stays a named table because the flank, the nose and the tail are one creature
// drawn by three separate functions, and a yellow that drifted between them would show as a
// seam down the corner of the vehicle.
export const MINION = {
  yellow: '#FFD000',
  /** The roofline shade, so a flank two metres tall is not one flat fill. */
  yellowShade: '#E0B400',
  denim: '#284E8A',
  denimDark: '#1E3A6B',
  goggle: '#B8BCC2',
  goggleDark: '#8A8E95',
  strap: '#3A3D42',
  eyeWhite: '#F4F4F0',
  pupil: '#5C381E',
  mouth: '#261E1A',
  hair: '#141414',
  boot: '#22252B',
} as const

/**
 * `PANEL` in sheet pixels: a whole figure drawn in the vehicle's own measurements.
 *
 * `bandX`/`bandY` above already convert a position; this adds the two things a SHAPE needs
 * — pixels for a LENGTH on each axis — and is the reason a minion is round on both bodies.
 * One 1024 × 128 sheet serves two differently-shaped panels, ~58 × 57 px/m on the R160 but
 * ~84 × 49 px/m on the Xcelsior, so a circle drawn as a circle on the sheet arrives as a
 * vertical ellipse on the bus (the 1.72× stretch in `docs/authoring-liveries.md`).
 * Converting per axis makes that stretch disappear at the source, rather than asking every
 * shape to carry a correction the next body would invalidate.
 */
function panelScale(w: number, h: number, body: PanelBody) {
  const { x0, x1, z0, z1 } = PANEL[body]
  return {
    /** Position x in metres, +x forward → x across the sheet. */
    x: (m: number) => bandX(body, m) * w,
    /** Height z in metres above rail/road → y down the sheet. */
    y: (m: number) => bandY(body, m) * h,
    /** A horizontal length in metres → px. */
    dx: (m: number) => (m / (x1 - x0)) * w,
    /** A vertical length in metres → px. */
    dy: (m: number) => (m / (z1 - z0)) * h,
    /** The panel's own extents, so a repeat can space itself along the real body. */
    x0, x1, z0, z1,
  }
}

type Scale = ReturnType<typeof panelScale>

/**
 * Hands the FRONT door's glazing back to the glass, for a bus wrap that claims `windows`.
 *
 * The one opening a real wrap leaves clear is the door the operator boards riders through,
 * and `windows` cannot stop there — see `BUS_GLASS`. So the panes are repainted their own
 * colour after the artwork has run over them.
 *
 * Drawn per PANE, not as one box over the doorway: the mullion between the two leaves is
 * door LEAF and stays wrapped, so a rectangle across the opening would black out a 21 cm
 * stripe of paint with the glass. (The diagonal rail each leaf carries across its own pane
 * IS inside these polygons and does go dark with it — it shares the glazing's texels, being
 * at the same station on the same unwrap, and at 5.6 cm wide it is under 5 px of sheet.)
 *
 * ⚠️ Right flank only, and the CALLER decides that: the doors are on the vehicle's right.
 */
function busFrontDoorGlass(ctx: CanvasRenderingContext2D, s: Scale) {
  ctx.fillStyle = BUS_GLASS
  for (const pane of BUS_FRONT_DOOR_PANES) {
    ctx.beginPath()
    for (const [x, z] of pane) ctx.lineTo(s.x(x), s.y(z))
    ctx.closePath()
    ctx.fill()
  }
}

/**
 * Leaves both door leaves UNPAINTED — white, with their glazing dark — after the flank's
 * artwork has already run across them.
 *
 * A painted scheme is paint on the BODY. The doors on this fleet are white leaves with
 * black glass and nothing else: no belt crossing them, no agency decal on them. Blocking
 * them back out here rather than dropping `doors` from the surface list is deliberate and
 * is the difference between two near-identical-sounding things:
 *
 *   • un-claiming `doors` leaves the leaf its AUTHORED colour, which is near-black
 *     (`MAT_LIVERY_DOOR` at 0.09/0.10/0.11) — a black door, not a white one;
 *   • claiming it and painting it white gives a white door, and lets the glazing keep the
 *     window band's own dark rather than the white the sidewall would otherwise hand it
 *     below 1.25 m, which is where the leaf's tall panes reach.
 *
 * So the surface is claimed precisely so it can be painted plainly.
 *
 * ⚠️ Right flank only, and the CALLER decides that, exactly as for `busFrontDoorGlass`:
 * `DOORS_RIGHT` puts both doors on the vehicle's right, and at those two stations the far
 * flank carries the driver's window (forward) and an ordinary passenger bay (centre) —
 * glazing either way, and never a door leaf.
 */
function busDoorsPlain(ctx: CanvasRenderingContext2D, s: Scale) {
  const [leafLo, leafHi] = BUS_DOOR_LEAF_Z
  ctx.fillStyle = BUS_WHITE
  for (const [x0, x1] of BUS_DOOR_BAYS) {
    ctx.fillRect(s.x(x0), s.y(leafHi), s.dx(x1 - x0), s.dy(leafHi - leafLo))
  }
  ctx.fillStyle = WINDOW_MASK
  for (const pane of [...BUS_FRONT_DOOR_PANES, ...BUS_CENTRE_DOOR_PANES]) {
    ctx.beginPath()
    for (const [x, z] of pane) ctx.lineTo(s.x(x), s.y(z))
    ctx.closePath()
    ctx.fill()
  }
}

/** An ellipse on the sheet that is a CIRCLE of radius `rm` metres on the vehicle. */
function disc(
  ctx: CanvasRenderingContext2D, s: Scale,
  xm: number, ym: number, rm: number,
  fill: string, stroke?: string, strokeM = 0.03,
) {
  ctx.beginPath()
  ctx.ellipse(s.x(xm), s.y(ym), s.dx(rm), s.dy(rm), 0, 0, Math.PI * 2)
  ctx.fillStyle = fill
  ctx.fill()
  if (stroke) {
    ctx.lineWidth = s.dy(strokeM)
    ctx.strokeStyle = stroke
    ctx.stroke()
  }
}

/**
 * Path for a `wm × hm` metre box centred on `xm` with its base at `ym`, corners rounded by
 * `rm`. Built from quadratic corners rather than `roundRect` so it does not depend on a
 * canvas API the older WebViews this ships into do not all carry. The caller fills.
 */
function capsulePath(
  ctx: CanvasRenderingContext2D, s: Scale,
  xm: number, ym: number, wm: number, hm: number, rm: number,
) {
  const x0 = s.x(xm - wm / 2), x1 = s.x(xm + wm / 2)
  const yTop = s.y(ym + hm), yBot = s.y(ym)
  const rx = s.dx(rm), ry = s.dy(rm)
  ctx.beginPath()
  ctx.moveTo(x0 + rx, yTop)
  ctx.lineTo(x1 - rx, yTop)
  ctx.quadraticCurveTo(x1, yTop, x1, yTop + ry)
  ctx.lineTo(x1, yBot - ry)
  ctx.quadraticCurveTo(x1, yBot, x1 - rx, yBot)
  ctx.lineTo(x0 + rx, yBot)
  ctx.quadraticCurveTo(x0, yBot, x0, yBot - ry)
  ctx.lineTo(x0, yTop + ry)
  ctx.quadraticCurveTo(x0, yTop, x0 + rx, yTop)
  ctx.closePath()
}

type MinionOpts = {
  /** One goggle or two. Alternating them stops a row of stamps reading as wallpaper. */
  eyes: 1 | 2
  /** Hair sprigs above the head. 0 is bald, which most of them are. */
  sprigs?: number
  /** This one's own goggle strap. Off where a continuous strap already runs the body. */
  strap?: boolean
}

/**
 * One minion, authored entirely in the vehicle's own metres: `xm` is its centre along the
 * body (+x forward, as `PANEL` measures), `feetM` the height above rail/road it stands at,
 * `tallM` how tall it is.
 *
 * Its pupils look toward the sheet's `+x`, and it takes no `flank` argument, so it looks
 * the same way as seen from OUTSIDE either side — which for a crowd of faces is the thing
 * that matters. It is not the nose on both: the left island is mirrored (see `draw`), so
 * the crowd is looking forward on the right flank and aft on the left. Making them all
 * face the nose would mean handing them, and a minion that faced away from you on one side
 * of the bus would be the worse bug.
 */
function minion(
  ctx: CanvasRenderingContext2D, s: Scale,
  xm: number, feetM: number, tallM: number,
  { eyes, sprigs = 0, strap = true }: MinionOpts,
) {
  const t = tallM
  const bodyW = 0.62 * t
  const eyeY = feetM + 0.70 * t

  // Hair first: the head's own edge then covers where the sprigs root.
  if (sprigs) {
    ctx.strokeStyle = MINION.hair
    ctx.lineWidth = s.dy(0.035 * t)
    ctx.lineCap = 'round'
    for (let i = 0; i < sprigs; i++) {
      const off = (i - (sprigs - 1) / 2) * 0.13 * t
      ctx.beginPath()
      ctx.moveTo(s.x(xm + off), s.y(feetM + 0.97 * t))
      ctx.quadraticCurveTo(
        s.x(xm + off + 0.05 * t), s.y(feetM + 1.09 * t),
        s.x(xm + off - 0.03 * t), s.y(feetM + 1.15 * t),
      )
      ctx.stroke()
    }
  }

  capsulePath(ctx, s, xm, feetM, bodyW, t, bodyW / 2)
  ctx.fillStyle = MINION.yellow
  ctx.fill()

  // Overalls and boots, clipped to the body so denim cannot spill past the yellow edge and
  // the capsule's rounded base trims the boots into feet.
  ctx.save()
  capsulePath(ctx, s, xm, feetM, bodyW, t, bodyW / 2)
  ctx.clip()
  ctx.fillStyle = MINION.denim
  ctx.fillRect(s.x(xm - bodyW), s.y(feetM + 0.42 * t), s.dx(bodyW * 2), s.dy(0.42 * t))
  ctx.fillRect(s.x(xm - 0.22 * t), s.y(feetM + 0.58 * t), s.dx(0.44 * t), s.dy(0.16 * t))
  ctx.lineWidth = s.dy(0.05 * t)
  ctx.strokeStyle = MINION.denim
  for (const dir of [-1, 1]) {
    ctx.beginPath()
    ctx.moveTo(s.x(xm + dir * 0.19 * t), s.y(feetM + 0.58 * t))
    ctx.lineTo(s.x(xm + dir * 0.27 * t), s.y(feetM + 0.84 * t))
    ctx.stroke()
  }
  ctx.fillStyle = MINION.denimDark
  ctx.fillRect(s.x(xm - 0.10 * t), s.y(feetM + 0.53 * t), s.dx(0.20 * t), s.dy(0.02 * t))
  ctx.fillStyle = MINION.boot
  ctx.fillRect(s.x(xm - bodyW), s.y(feetM + 0.07 * t), s.dx(bodyW * 2), s.dy(0.07 * t))
  ctx.restore()

  if (strap) {
    ctx.fillStyle = MINION.strap
    ctx.fillRect(
      s.x(xm - bodyW / 2 - 0.02 * t), s.y(eyeY + 0.05 * t),
      s.dx(bodyW + 0.04 * t), s.dy(0.10 * t),
    )
  }

  if (eyes === 1) {
    disc(ctx, s, xm, eyeY, 0.22 * t, MINION.goggle, MINION.goggleDark, 0.03 * t)
    disc(ctx, s, xm, eyeY, 0.165 * t, MINION.eyeWhite)
    disc(ctx, s, xm + 0.035 * t, eyeY, 0.07 * t, MINION.pupil)
  } else {
    for (const dir of [-1, 1]) {
      const cx = xm + dir * 0.155 * t
      disc(ctx, s, cx, eyeY, 0.155 * t, MINION.goggle, MINION.goggleDark, 0.028 * t)
      disc(ctx, s, cx, eyeY, 0.11 * t, MINION.eyeWhite)
      disc(ctx, s, cx + 0.025 * t, eyeY, 0.048 * t, MINION.pupil)
    }
  }

  // A smile: the control point sits BELOW the ends, and `s.y` inverts, so the curve dips.
  ctx.beginPath()
  ctx.strokeStyle = MINION.mouth
  ctx.lineWidth = s.dy(0.045 * t)
  ctx.lineCap = 'round'
  const my = feetM + 0.50 * t
  ctx.moveTo(s.x(xm - 0.13 * t), s.y(my + 0.035 * t))
  ctx.quadraticCurveTo(s.x(xm), s.y(my - 0.055 * t), s.x(xm + 0.13 * t), s.y(my + 0.035 * t))
  ctx.stroke()
}

/**
 * The yellow field every minion stands on, with a shade under the roofline and another
 * along the skirt. Two metres of one flat fill reads as a decal; the shades give the flank
 * the same top-and-bottom falloff the unpainted body gets from its own curvature.
 */
function minionField(ctx: CanvasRenderingContext2D, s: Scale, w: number, h: number) {
  ctx.fillStyle = MINION.yellow
  ctx.fillRect(0, 0, w, h)
  ctx.fillStyle = MINION.yellowShade
  ctx.fillRect(0, 0, w, s.dy(0.14))
  ctx.fillRect(0, s.y(s.z0 + 0.06), w, s.dy(0.06))
}

// ── The R160's blue band ──────────────────────────────────────────────────────────────
// The agency scheme as the TRAINS wear it: bare stainless below the belt moulding, MTA blue
// from there to the roof, and — on the cab car only — the gold-and-white diagonal on the
// pillar behind the operator's window.
//
// This replaced a blue-and-gold sine wave down the whole flank, and the reason is the same
// one that took the wave off the bus: the wave was a shape nobody's train has ever worn,
// invented for a sheet rather than measured off a body. What is here now is the design the
// user described against a platform photograph of car 8540, drawn in the metres the car's
// own features are at, so the band breaks on the moulding and the diagonal fills the pillar.
//
// ⚠️ **Sides only, and that costs something on this map.** Nothing sets a camera pitch, so
// a vehicle mostly presents its ROOF — which is why `shellColor` exists and why every bus
// livery must set it. This one deliberately does not: an R160's roof is bare stainless in
// reality, the user asked for "the sides and only sides", and painting the lid to make the
// livery easier to see from straight down would be inventing the vehicle to suit the camera.
// `farColor` is where the scheme survives being looked at from a distance instead.
//
// ⚠️ The two bodies are DIFFERENT DRAWINGS, which is what `body` was threaded into `draw`
// for. The B car takes the band end to end, square at both corners. The A car takes it from
// the rear forward to a RAKED edge on the mid-car pillar, and repeats that rake at its cab
// as a three-stripe flash — blue, gold and white. One angle, `R160_SLANT_SLOPE`, serves
// both, so the car reads as carrying one motif twice rather than two near-misses.

/**
 * The blue field: aft of a forward edge that runs from `foreTopXm` at the roofline to
 * `foreBotXm` at the top of the belt moulding, back to the panel's rear corner.
 *
 * Two x's rather than one because the A car's edge is RAKED and the B car's is square —
 * passing the same value twice is what makes a square edge, so the two bodies differ by
 * their arguments rather than by a branch in here.
 *
 * ⚠️ HANDED, and it has to be — this is the trap the whole `flankX` doc is about. The band
 * is anchored to an END of the car on the A car, and `x = w` is one end of the body on the
 * right island and the OTHER on the left one. Drawn through `s.x` the A car's band would
 * cover the rear half of one flank and the CAB half of the other, which is invisible from
 * any single view of the train. The B car's band is symmetric and would not have cared,
 * which is exactly why the handedness belongs in the shared helper rather than at one call.
 *
 * The lower edge is the moulding's TOP, so the rib itself stays bare: paint masked off at a
 * body line, rather than a fill that happens to stop near one.
 */
function r160BlueBand(
  ctx: CanvasRenderingContext2D, s: Scale, w: number, flank: LiveryFlank,
  foreTopXm: number, foreBotXm: number,
) {
  const at = flankX(s, w, flank)
  // y = 0 is the roofline, so the moulding's own sheet row is the band's floor.
  const floor = s.y(R160_BELT_Z[1])
  ctx.beginPath()
  ctx.moveTo(at(foreTopXm), 0)
  ctx.lineTo(at(s.x1), 0)
  ctx.lineTo(at(s.x1), floor)
  ctx.lineTo(at(foreBotXm), floor)
  ctx.closePath()
  ctx.fillStyle = MTA_BLUE
  ctx.fill()
}

/**
 * Where the A car's band stops: the raked edge's x at the roofline and at the moulding.
 *
 * Centred on the mid-car pillar and leaning at the shared slope, so the band's edge and the
 * cab flash are parallel. Exported as a pair rather than computed at the call site because
 * the test has to be able to ask where the edge went without re-deriving the geometry.
 */
function r160BandEdgeX(): [top: number, bottom: number] {
  const [fore, aft] = R160_A_MID_PILLAR_X
  const mid = (fore + aft) / 2
  // The band only spans the moulding to the roof, so its lean is the slope over THAT height,
  // not over the panel's.
  const lean = (R160_BELT_Z[1] - PANEL['r160-a'].z1) * R160_SLANT_SLOPE
  // Head aft, foot forward — the same sense as the cab flash. `lean` is negative (z1 is the
  // larger), so the head gets +half and the foot −half.
  return [mid - lean / 2, mid + lean / 2]
}

/**
 * The A car's cab flash: white, gold and a thick blue behind them, raked hard, sweeping
 * across the operator's window and back over the cab pillar.
 *
 * From the user's photograph of car 8540: the group leans FORWARD going down (its foot is
 * nearer the cab than its head) and gold sits aft of white. The blue is not in that
 * photograph (a real R160 has no blue anywhere) and is this livery's own: the band's colour
 * brought forward to the cab so the flash reads as the band's terminating edge repeated,
 * rather than as an unrelated decal on a stainless nose.
 *
 * **It is anchored to the WINDOW, and that is the whole geometry here.** The fore edge is
 * produced through the centre of `R160_A_CAB_WINDOW` at the shared slope, so it grazes into
 * the aperture's aft-top corner (~86 mm of the window's 0.451 m still covered at the header,
 * ~10 mm at the roofline), runs behind the glazing, and comes out under the sill covering
 * most of the window's width. That is what "intersect a little bit of the first window"
 * resolves to on a body whose window is a HOLE: nothing draws inside the aperture, so the
 * only visible intersection is the wedge above the header and the sweep below the sill, and
 * between them the flash simply passes behind the glass — which is what a wrap over a window
 * frame actually looks like.
 *
 * ⚠️ The anchor is deliberately a point the paint cannot land on. Pinning the foot to a jamb
 * instead, which is what this did before, cannot produce that: the rake leans forward going
 * down and the window is at the TOP of the panel, so a foot pinned at the jamb puts the
 * group's forward-most metre down at the skirt, a metre below the glazing, and the flash
 * never comes near the window at the window's own height however wide it gets.
 *
 * From the mesh: the two bounds. Forward, `R160_A_CAB_FRONT_WALL` — the foot lands at 8.3836,
 * which is 0.237 m inside the panel's own fore edge at 8.6210 and, as it happens, within a
 * millimetre of that wall's midpoint. Aft, `R160_A_FIRST_DOOR_PANE_X` — the aft edge's
 * furthest point is 6.665 at the roofline, where the panel is solid all the way across, and
 * at every glazing height it is aft of 6.74, clear of the door's forward pane at 6.5870.
 * Outside those the surface is somebody else's or nobody's, and a stripe that overshoots
 * does not spill, it stops existing.
 *
 * Widths are the photograph's proportions (19% / 28% / 53%) scaled to 1.045 m — the blue
 * still the thick one behind the two accents. With the lean that is a 1.719 m footprint
 * against the 0.425 m this started at, and it deliberately runs under the MTA roundel
 * placard (7.1875…7.6825, `R160_CAB_PILLAR_X` ± half of `R160_ROUNDEL_SIZE` in
 * `r160Model.ts`, pushed hard against the operator's window because the door-open light and
 * key flap own the middle of the pillar). That is free: the roundel is a separate
 * `TrainSideIconLayer` quad, so it draws over the stripes cleanly.
 *
 * ⚠️ Cab at −x. The A car is modelled with its cab at the negative end
 * (`R160_CAB_FORWARD_YAW` is the half-turn that puts it forward at runtime), so the window's
 * unsigned metres are negated here, and "aft" is toward +x.
 */
function r160CabStripes(
  ctx: CanvasRenderingContext2D, s: Scale, w: number, h: number, flank: LiveryFlank,
) {
  const at = flankX(s, w, flank)
  const panel = PANEL['r160-a']
  const lean = (panel.z1 - panel.z0) * R160_SLANT_SLOPE
  // The forward edge, produced up and down from the window's own centre rather than pinned
  // to a jamb. `rise` is how far up the panel that centre sits, so the foot is that share of
  // the lean forward of it and the head the remaining share aft — which is what keeps the
  // edge passing through the aperture no matter what the slope is changed to.
  const centreX = (R160_A_CAB_WINDOW.x[0] + R160_A_CAB_WINDOW.x[1]) / 2
  const centreZ = (R160_A_CAB_WINDOW.z[0] + R160_A_CAB_WINDOW.z[1]) / 2
  const rise = (centreZ - panel.z0) / (panel.z1 - panel.z0)
  let head = -(centreX - (1 - rise) * lean)
  let foot = -(centreX + rise * lean)

  for (const [widthM, colour] of [
    [0.200, R160_STRIPE_WHITE],
    [0.289, R160_STRIPE_GOLD],
    [0.556, MTA_BLUE],
  ] as const) {
    // Aft is toward +x, and the cab end is negative, so each stripe is laid on the last by
    // ADDING its width.
    const headAft = head + widthM
    const footAft = foot + widthM
    ctx.beginPath()
    ctx.moveTo(at(head), 0)
    ctx.lineTo(at(headAft), 0)
    ctx.lineTo(at(footAft), h)
    ctx.lineTo(at(foot), h)
    ctx.closePath()
    ctx.fillStyle = colour
    ctx.fill()
    head = headAft
    foot = footAft
  }
}

// ── The classic scheme ────────────────────────────────────────────────────────────────
// White body, a dark band around the windows, a blue belt under them, and the flag. The
// two bands are the whole livery, and both are written in the metres they were MEASURED
// at (see PANEL), because their whole job is to line up with glazing that is a different
// height on each body.

type ClassicBands = {
  /** The window mask, [bottom, top] in metres. Measured against the real glazing. */
  maskZ: readonly [number, number]
  /**
   * How far along the body the mask runs, [rear, nose]. Required, and it used to be
   * optional for the subway car this scheme no longer dresses: a bus's glazing stops short
   * of both ends, so a mask painted to the panel's own edges blacks out the corners fore
   * and aft of the glass.
   */
  maskX: readonly [number, number]
  /** The belt stripe, [bottom, top] in metres. */
  beltZ: readonly [number, number]
}

/**
 * The white field, the window mask and the belt — in that order, so each covers the last.
 *
 * ⚠️ The mask takes `flank`, because it does not reach the same distance from both ends:
 * the glazing runs −5.10 to 6.06 in a panel that spans −6.10 to 6.17, so the band is 0.96 m
 * nearer the nose than the tail. Put through `s.x` — which is what it did — that asymmetry
 * MIRRORS on the left island, and the band lands most of a metre down the body: overhanging
 * the tail corner on one side of every bus and stopping short of the front door on the
 * other, on a body whose glazing is in neither place. It is the same rule the flag and the
 * wordmark follow, and the reason `flankX` exists; a band is just easy to mistake for the
 * kind of full-width marking that genuinely does not care.
 */
function classicScheme(
  ctx: CanvasRenderingContext2D, s: Scale, w: number, h: number,
  flank: LiveryFlank, bands: ClassicBands,
) {
  ctx.fillStyle = BUS_WHITE
  ctx.fillRect(0, 0, w, h)

  const [maskLo, maskHi] = bands.maskZ
  const at = flankX(s, w, flank)
  const xa = at(bands.maskX[0])
  const xb = at(bands.maskX[1])
  const x0 = Math.min(xa, xb)
  const x1 = Math.max(xa, xb)
  ctx.fillStyle = WINDOW_MASK
  ctx.fillRect(x0, s.y(maskHi), x1 - x0, s.dy(maskHi - maskLo))

  const [beltLo, beltHi] = bands.beltZ
  ctx.fillStyle = MTA_BLUE
  ctx.fillRect(0, s.y(beltHi), w, s.dy(beltHi - beltLo))
}

/** The flag's own proportion, 1.9 : 1. `panelScale` is what keeps it that on both bodies. */
const FLAG_RATIO = 1.9

/**
 * A US flag decal, its NOSE-most edge at `noseXm` and its bottom at `baseZm`.
 *
 * ⚠️ **This one is HANDED and has to be**, which is why it takes a `flank`. The union leads
 * — the flag flies back from its staff as the vehicle moves, which is the convention a real
 * vehicle decal follows and the reason the reversed flag on a plane's right side is not a
 * mistake. But `x = w` is the nose on the right flank and the rear on the left one (see
 * `draw`), so a canton pinned to the sheet's high edge would lead on one side and trail on
 * the other. `sheetX` puts the whole decal in body space; the canton then goes on whichever
 * sheet edge the nose is actually at.
 */
function flagDecal(
  ctx: CanvasRenderingContext2D, s: Scale, w: number, flank: LiveryFlank,
  noseXm: number, baseZm: number, tallM: number,
) {
  const wm = tallM * FLAG_RATIO
  const topZ = baseZm + tallM
  const stripeM = tallM / 13
  const sheetX = (m: number) => (flank === 'right' ? s.x(m) : w - s.x(m))
  const noseEdge = sheetX(noseXm)
  const tailEdge = sheetX(noseXm - wm)
  const left = Math.min(noseEdge, tailEdge)
  const width = Math.abs(noseEdge - tailEdge)

  // 13 stripes, red at both edges — which is also what keeps the flag's own outline off
  // the white body along its top and bottom.
  for (let i = 0; i < 13; i++) {
    ctx.fillStyle = i % 2 === 0 ? FLAG_RED : FLAG_WHITE
    ctx.fillRect(left, s.y(topZ - i * stripeM), width, s.dy(stripeM))
  }

  const cantonW = wm * 0.4
  const cantonH = stripeM * 7
  const cantonSheetW = s.dx(cantonW)
  const cantonLeft = noseEdge < tailEdge ? noseEdge : noseEdge - cantonSheetW
  ctx.fillStyle = FLAG_BLUE
  ctx.fillRect(cantonLeft, s.y(topZ), cantonSheetW, s.dy(cantonH))

  // The stars are a speckle at this size — a 5 × 4 grid of dots roughly half a stripe
  // across. Drawn anyway: the canton reads as a flag's canton with them and as a plain
  // blue corner without.
  ctx.fillStyle = FLAG_WHITE
  const dotM = stripeM * 0.55
  const dotW = s.dx(dotM)
  for (let row = 0; row < 4; row++) {
    for (let col = 0; col < 5; col++) {
      // Already in sheet space — the canton's own box was placed there — so only the
      // vertical half of this goes back through the metre converters.
      const cx = cantonLeft + ((col + 0.5) / 5) * cantonSheetW
      const cz = topZ - ((row + 0.5) / 4) * cantonH
      ctx.fillRect(cx - dotW / 2, s.y(cz + dotM / 2), dotW, s.dy(dotM))
    }
  }

  // A hairline, or the white stripes dissolve into the white body at the trailing edge.
  ctx.strokeStyle = '#9AA0A8'
  ctx.lineWidth = s.dy(0.012)
  ctx.strokeRect(left, s.y(topZ), width, s.dy(tallM))
}

/** The agency's own words beside the roundel, on the belt and again on the tail. */
const NYC_BUS = 'New York City Bus'

/**
 * The agency's mark in the belt: the roundel FIRST, then "New York City Bus" reading away
 * from it, both white on the blue.
 *
 * **The roundel leads.** It used to trail — the label was drawn `textAlign: 'right'` back
 * from the disc, so the mark read "New York City Bus ⬤" — and the reference has it the
 * other way on every view that carries it, flank and tail alike. It is the agency's
 * lock-up, not two decals that happen to be adjacent.
 *
 * **And it is HANDED**, which it was not. The old comment argued that one unhanded drawing
 * lands near the front on one flank and near the rear on the other because that is what a
 * single printed decal does — but a decal is applied to each side separately, and a real
 * bus wears this one forward on BOTH sides. So the group is anchored to the nose through
 * `flankX` like everything else that names an end.
 *
 * What is *not* handed is the reading order, and that falls out for free: sheet x always
 * runs in the direction that flank reads (the exporters' mirror is what buys that), so
 * "disc then words" is drawn once and arrives correct on both sides. It simply puts the
 * disc at the group's nose end on the left flank and at its tail end on the right — which
 * is what a real pair of decals does, and is visible in the reference's own two side views.
 *
 * ⚠️ Text has to be drawn through the STRETCH. A glyph is the one thing on this sheet whose
 * shape is not negotiable, and the sheet is 83.5 px/m across the bus against 48.5 px/m up
 * it: set at the height it should be and it comes out condensed to 58% on the body. So the
 * font is sized in the vertical scale and the context is scaled horizontally to match,
 * which is the same correction `panelScale` applies to everything else. It is also why the
 * group's own length has to be MEASURED before it can be placed — `measureText` answers in
 * the scaled space, so the width comes back through `stretch` to reach body metres.
 *
 * The fleet number beside it in the real livery is deliberately absent: one patched model
 * is shared by the whole fleet, so a number here would put the same one on every bus. That
 * is the standing rule about per-vehicle markings — they are placards, not liveries.
 */
function mtaWordmark(
  ctx: CanvasRenderingContext2D, s: Scale, w: number, flank: LiveryFlank,
  noseXm: number, centreZm: number, rM: number, emM: number,
) {
  const stretch = s.dx(1) / s.dy(1)
  const gapM = rM * 0.5

  // Measure first, place second. The label is set in the horizontally-scaled space, so its
  // width comes back multiplied by `stretch` before it means anything in sheet pixels.
  ctx.font = canvasBulletFont(s.dy(emM))
  const labelM = (ctx.measureText(NYC_BUS).width * stretch) / s.dx(1)
  const groupM = 2 * rM + gapM + labelM

  // The whole lock-up sits in body space, `noseXm` at its forward edge, and only then is
  // asked which sheet edge that end is on — the `flagDecal` pattern, for the same reason.
  const at = flankX(s, w, flank)
  const left = Math.min(at(noseXm), at(noseXm - groupM))
  const discX = left + s.dx(rM)

  ctx.beginPath()
  ctx.ellipse(discX, s.y(centreZm), s.dx(rM), s.dy(rM), 0, 0, Math.PI * 2)
  ctx.fillStyle = BUS_WHITE
  ctx.fill()

  ctx.save()
  ctx.textBaseline = 'middle'
  ctx.scale(stretch, 1)
  // The bundled Helvetica clone, not the body face — MTA signage is Helvetica and this
  // wordmark is the agency's own. Same family every bullet on this map is set in.
  ctx.font = discFont(ctx, 'MTA', s.dy(rM))
  ctx.fillStyle = MTA_BLUE
  ctx.textAlign = 'center'
  ctx.fillText('MTA', discX / stretch, s.y(centreZm))

  ctx.font = canvasBulletFont(s.dy(emM))
  ctx.fillStyle = BUS_WHITE
  ctx.textAlign = 'left'
  ctx.fillText(NYC_BUS, (left + s.dx(2 * rM + gapM)) / stretch, s.y(centreZm))
  ctx.restore()
}

/**
 * A font size that keeps `text` inside a disc of `radiusPx`, measured rather than guessed:
 * "MTA" in this face runs about 2.1 em, so a size picked off the radius alone overflows the
 * circle it is meant to sit in. Call inside the horizontally-scaled context, where the
 * disc is a circle and `radiusPx` is its radius in both axes.
 */
function discFont(ctx: CanvasRenderingContext2D, text: string, radiusPx: number): string {
  ctx.font = canvasBulletFont(radiusPx)
  const width = ctx.measureText(text).width
  const max = radiusPx * 1.5
  return canvasBulletFont(width > max ? radiusPx * (max / width) : radiusPx)
}

/**
 * The agency roundel on an end cap, `rM` metres across the radius: a filled circle with
 * MTA's own lettering in it. Which way round the two colours go is the caller's, because
 * the drawing has it white-on-blue in the front band and blue-on-white on the plain tail.
 *
 * A cap sheet is nearly square, but `capScale`'s `dx`/`dy` are still what keep this a
 * circle rather than a 1.14 : 1 ellipse.
 */
function capRoundel(
  ctx: CanvasRenderingContext2D, c: ReturnType<typeof capScale>,
  ym: number, zm: number, rM: number,
  { fill = BUS_WHITE, ink = MTA_BLUE }: { fill?: string; ink?: string } = {},
) {
  ctx.beginPath()
  ctx.ellipse(c.x(ym), c.y(zm), c.dx(rM), c.dy(rM), 0, 0, Math.PI * 2)
  ctx.fillStyle = fill
  ctx.fill()

  const stretch = c.dx(1) / c.dy(1)
  ctx.save()
  ctx.scale(stretch, 1)
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.font = discFont(ctx, 'MTA', c.dy(rM))
  ctx.fillStyle = ink
  ctx.fillText('MTA', c.x(ym) / stretch, c.y(zm))
  ctx.restore()
}

// ── The bus wrap ──────────────────────────────────────────────────────────────────────
// Drawn from the two flank photographs added alongside this code:
// `reference/BUS-XCEL-40/XE40_photo_mta_side_offside.png` is the bus's LEFT flank (no
// passenger doors, nose to the viewer's left) and `..._side_doorside.png` its RIGHT (both
// door leaves visible, nose to the viewer's right). Between them they show the whole wrap,
// which no reference in the folder did before.
//
// ⚠️ **These figures are placed by eye. They are NOT calibrated**, and that is the one way
// this differs from `classic.bus`. Both photographs are three-quarter views of a bus in
// traffic, so there is no honest conversion from a pixel row to a body metre the way
// CLASSIC_REFERENCE has one. What the photographs DO settle is the design — which elements
// exist, what colour each is, which end of the bus each belongs to, and their order along the
// flank — and that is what a rider actually reads at a stop.
//
// What was here before was three concentric arcs, gold/white/sky, on one centre behind the
// front cap. Every part of that is wrong against these photographs:
//  • nothing in the wrap is concentric, and nothing is a thin outlined arc;
//  • there is no WHITE in the paint at all — the white on a real bus is lettering
//    ("clean-energy bus", "HYBRIDBUS", the fleet number, the roundel). The band that read as
//    white in the low-resolution front-three-quarter shot is cyan, and the pale value beside
//    it is `WRAP_GOLD_PALE`;
//  • it lived entirely in the 0.62 m of wall above the window header. The real wrap runs the
//    full length of the flank and most of it is BELOW the sill — which is the half of the bus
//    a rider standing at the kerb is closest to.
//
// The wrap is four elements. Front to rear, and in paint order:
//  1. `busPinstripeFan`   — fine cyan pinstripes over the whole lower front flank;
//  2. `busLowerSweep`     — a gold sweep bellying down the lower flank, cyan on its leading
//                           edge;
//  3. `busRearStripeFan`  — raked gold stripes over the rear quarter, running off the tail;
//  4. `busShoulderSweep`  — a cyan wedge on the window header at the front under a pale gold
//                           arc, carrying the fleet number on the real bus.
//
// The two flanks are drawn the SAME here. On the real vehicle they are close but not mirror
// images — the wrap is cut per side around the doors — and matching that would need an
// elevation of each, which these two photographs are not.

/** The side glazing's [sill, header], named for the wrap's functions to read against. */
const [BUS_SILL_Z, BUS_HEADER_Z] = BUS_GLAZING_Z

/**
 * The cyan wedge on the front shoulder, capped by the pale gold arc — element 4.
 *
 * Its lower edge is the window header ITSELF, not a line above it: in
 * `XE40_photo_mta_side_offside.png` the cyan sits down on the glazing's top rail, and that
 * contact is what ties the graphic to the windows instead of leaving it floating on the roof
 * shoulder. The arc over it crests just under the panel's own top edge (2.947) and comes back
 * down to the header at both ends, so the wedge is a lens rather than a band.
 *
 * Handed, because it is anchored to the nose: see `flankX`.
 */
function busShoulderSweep(
  ctx: CanvasRenderingContext2D, s: Scale, w: number, flank: LiveryFlank,
) {
  const at = flankX(s, w, flank)
  const nose = 5.95, tail = -0.4, crest = 5.2

  // One control point serves the fill and the arc over it, so the two cannot drift apart —
  // the same reason the old drawing put its three arcs on one centre. Endpoints at the header
  // with the control at 3.30 puts the crest at 2.81 m, a hand's breadth under the roof line.
  //
  // The control sits well FORWARD of the wedge's midpoint on purpose. A quadratic crests at
  // the average of its ends and twice its control, so 5.2 here lands the deepest cyan at
  // x = 3.99 m — a third of the way back from the nose — and leaves a long shallow taper
  // running aft. That asymmetry is the shape in the photograph; a control at the midpoint
  // gives a symmetric lens, which is what this drew first and reads as a decal rather than a
  // sweep coming off the front cap.
  const lens = () => {
    ctx.moveTo(at(tail), s.y(BUS_HEADER_Z))
    ctx.quadraticCurveTo(at(crest), s.y(3.3), at(nose), s.y(BUS_HEADER_Z))
  }

  ctx.beginPath()
  lens()
  ctx.closePath()
  ctx.fillStyle = WRAP_CYAN
  ctx.fill()

  ctx.beginPath()
  lens()
  ctx.lineWidth = s.dy(0.13)
  ctx.strokeStyle = WRAP_GOLD_PALE
  ctx.lineCap = 'butt'
  ctx.stroke()
}

/**
 * The fan of fine cyan pinstripes over the lower front flank — element 1, and the one that
 * covers the most sheet.
 *
 * ~17 stripes at a 6 cm pitch, which at this sheet's 48.5 px/m is a stroke 1.5 px wide. That
 * is deliberate and not a bug: individually they are below what the texture can resolve, and
 * what arrives on the body is the lighter blue field the photograph shows from across a
 * street. Drawing them as a flat tint instead would lose the grain a rider sees up close, and
 * this is the vehicle riders stand next to.
 *
 * The field's rear edge is RAKED — the stripes nearest the sill run furthest aft — which is
 * what stops the fan ending on a vertical seam mid-flank where nothing on the body explains
 * one.
 */
function busPinstripeFan(
  ctx: CanvasRenderingContext2D, s: Scale, w: number, flank: LiveryFlank,
) {
  const at = flankX(s, w, flank)
  // Just under half the pitch is ink, which is the duty cycle the photograph shows. It is set
  // a shade wider than a true half because a 1.5 px stroke loses most of its edge to
  // antialiasing, and losing it symmetrically still leaves the field lighter than the paint.
  const PITCH = 0.062, WIDTH = 0.036

  ctx.strokeStyle = WRAP_CYAN
  // Near-horizontal, so the vertical converter is the right one for the stroke width. A raked
  // stripe could not use it — see `busRearStripeFan`, which is filled for exactly that reason.
  ctx.lineWidth = s.dy(WIDTH)
  ctx.lineCap = 'butt'
  for (let z = s.z0 + PITCH * 0.5; z < BUS_SILL_Z; z += PITCH) {
    const height = (z - s.z0) / (BUS_SILL_Z - s.z0)
    const rear = 0.3 - 1.25 * height
    ctx.beginPath()
    ctx.moveTo(at(s.x1), s.y(z))
    // A shallow sag rather than a ruled line: the real stripes follow the body's flow, and a
    // fan of dead-straight lines reads as a printed test pattern.
    ctx.quadraticCurveTo(at((s.x1 + rear) / 2), s.y(z - 0.05), at(rear), s.y(z + 0.02))
    ctx.stroke()
  }
}

/**
 * The gold sweep across the lower flank with cyan on its leading edge — element 2.
 *
 * A belly-down curve, not a wave: both photographs have it leaving the sill around mid-body,
 * dipping to its lowest over the rear axle, and rising again as it runs aft. The control point
 * puts the belly at 0.67 m, clear of the skirt.
 *
 * ⚠️ It STOPS at x = −3.4, well short of the tail, and that is not the curve running out of
 * room. Aft of there the flank belongs to `busRearStripeFan`, and the photographs show that
 * quarter as gold stripes on BLUE — so a sweep carried to the corner would floor the stripes
 * on a gold field and erase the element entirely, the two being the same colour.
 *
 * Cyan is painted FIRST and gold over it, offset down its leading edge far enough that the
 * cyan is not simply buried: gold's half-width is 0.22 m, so an offset smaller than that
 * leaves a sliver. At 0.34 the two share an edge and both read, which is how the pair looks in
 * `XE40_photo_mta_side_offside.png` — one two-tone ribbon, not two with body colour between.
 */
function busLowerSweep(
  ctx: CanvasRenderingContext2D, s: Scale, w: number, flank: LiveryFlank,
) {
  const at = flankX(s, w, flank)
  ctx.lineCap = 'butt'
  for (const [color, thickM, dx, dz] of [
    [WRAP_CYAN, 0.24, 0.35, -0.34],
    [MTA_YELLOW, 0.44, 0, 0],
  ] as const) {
    ctx.beginPath()
    ctx.moveTo(at(1.5 + dx), s.y(1.34 + dz))
    ctx.quadraticCurveTo(at(-1 + dx), s.y(0.1 + dz), at(-3.4 + dx), s.y(1.15 + dz))
    ctx.lineWidth = s.dy(thickM)
    ctx.strokeStyle = color
    ctx.stroke()
  }
}

/**
 * The raked gold stripes over the rear quarter — element 3.
 *
 * Sixteen stripes leaning back at the top — `XE40_photo_mta_side_doorside.png` is unambiguous
 * about the direction, and it is the one thing here worth stating twice, because a fan raked
 * the wrong way still looks like a fan. The rearmost run off the back of the panel on purpose:
 * the tail is solid yellow (`drawRear`), so they resolve into it rather than stopping short of
 * the corner.
 *
 * The fan is in TWO parts, which is what the photograph shows and what a single loop with one
 * height would miss. The first five run the full height of the panel, crossing the window band
 * and the wall above it. Every one forward of those is capped at the sill, so the fan carries
 * on below the glazing to nearly mid-body while the wall above it goes clean blue — which is
 * where the agency lock-up and "HYBRIDBUS" sit on the real vehicle.
 *
 * ⚠️ Filled quads, not strokes, unlike `busPinstripeFan`. A stroke has ONE width in sheet
 * pixels, and this sheet is 83.5 px/m across the bus against 48.5 px/m up it — so a raked line
 * given `s.dx(w)` arrives too narrow and given `s.dy(w)` too wide, in a proportion that
 * changes with the rake. Four corners in body metres have no such axis to pick.
 */
function busRearStripeFan(
  ctx: CanvasRenderingContext2D, s: Scale, w: number, flank: LiveryFlank,
) {
  const at = flankX(s, w, flank)
  // 1.30 m of rake over the panel's 2.64 m of height is 26° off vertical, measured off the
  // stripe edges in `XE40_photo_mta_side_doorside.png`. Ink is a shade under half the pitch,
  // which is the gold-to-blue ratio the same frame shows.
  const PITCH = 0.3, WIDTH = 0.13, RAKE = 1.3

  ctx.fillStyle = MTA_YELLOW
  for (let i = 0; i < 16; i += 1) {
    const foot = s.x0 + 0.6 + PITCH * i
    const top = Math.max(BUS_SILL_Z, s.z1 - i * 0.34)
    const head = foot - RAKE * ((top - s.z0) / (s.z1 - s.z0))
    ctx.beginPath()
    ctx.moveTo(at(foot), s.y(s.z0))
    ctx.lineTo(at(foot + WIDTH), s.y(s.z0))
    ctx.lineTo(at(head + WIDTH), s.y(top))
    ctx.lineTo(at(head), s.y(top))
    ctx.closePath()
    ctx.fill()
  }
}

/**
 * Every pane on this flank painted back to glass — bays and both door leaves' glazing.
 *
 * The price of claiming `windows`, and the reason a wrap that only wanted ONE decal on the
 * glass has to draw all of it. `windows` is a single material covering every aperture on the
 * vehicle, so the moment it is claimed the artwork underneath lands on the glazing too and the
 * bus gets blue windows. Painting the panes back afterwards is what leaves the glass looking
 * exactly as it did while making the rear bay addressable.
 *
 * ⚠️ Panes only, never the whole band. The pillars BETWEEN the apertures are sidewall and
 * carry the wrap — the gold stripe fan crosses them in
 * `XE40_photo_mta_side_doorside.png` — so a rectangle over the window band, which is what the
 * `classic` scheme deliberately draws, would grey out the wrap's own pillars here.
 *
 * Door glazing is included and the door LEAF is not: the leaf is `MAT_LIVERY_DOOR` and stays
 * wrapped, which is what the same photograph shows.
 */
function busGlazing(
  ctx: CanvasRenderingContext2D, s: Scale, w: number, flank: LiveryFlank,
) {
  const at = flankX(s, w, flank)
  const panes = [
    ...BUS_BAY_PANES[flank],
    // The doors are on the vehicle's RIGHT, so their glazing exists on that flank alone.
    ...(flank === 'right' ? [...BUS_FRONT_DOOR_PANES, ...BUS_CENTRE_DOOR_PANES] : []),
  ]
  ctx.fillStyle = BUS_GLASS
  for (const pane of panes) {
    ctx.beginPath()
    for (const [x, z] of pane) ctx.lineTo(at(x), s.y(z))
    ctx.closePath()
    ctx.fill()
  }
}

/** The heart's own red, which is the tourist board's and not the flag's. */
const NY_HEART_RED = '#E0393E'

/**
 * The "I ♥ NY" decal, centred on `centreXm` with cap height `tallM`.
 *
 * ⚠️ Laid out in SHEET x rather than body x, which is what makes it read forwards on both
 * flanks from one call — the same property `mtaWordmark` relies on and for the same reason:
 * the exporters' mirror means sheet x always runs in the direction that flank reads. Placed by
 * body x instead, the left flank would say "NY ♥ I".
 *
 * The heart is a two-curve path rather than a glyph because the character is not in the
 * bundled face, and drawing it through `at`/`s.y` per point is what keeps it a heart on a
 * sheet that is 83.5 px/m across the bus and 48.5 px/m up it.
 */
function iLoveNyDecal(
  ctx: CanvasRenderingContext2D, s: Scale, w: number, flank: LiveryFlank,
  centreXm: number, centreZm: number, tallM: number,
) {
  const at = flankX(s, w, flank)
  const stretch = s.dx(1) / s.dy(1)
  const halfM = tallM * 1.35
  // Body space first, then ask which sheet edge each end landed on.
  const lead = Math.min(at(centreXm - halfM), at(centreXm + halfM))
  const span = Math.abs(at(centreXm + halfM) - at(centreXm - halfM))
  const heartX = lead + span * 0.42
  const heartR = s.dx(tallM * 0.58)

  ctx.save()
  ctx.textBaseline = 'middle'
  ctx.textAlign = 'center'
  ctx.scale(stretch, 1)
  ctx.font = canvasBulletFont(s.dy(tallM))
  ctx.fillStyle = FLAG_WHITE
  ctx.fillText('I', (lead + span * 0.08) / stretch, s.y(centreZm))
  ctx.fillText('NY', (lead + span * 0.79) / stretch, s.y(centreZm))
  ctx.restore()

  // Two mirrored curves meeting at the notch on top and the point below, drawn about the
  // heart's own centre so the lobes cannot land at different heights.
  const top = s.y(centreZm + tallM * 0.30)
  const notch = s.y(centreZm + tallM * 0.12)
  const point = s.y(centreZm - tallM * 0.52)
  ctx.beginPath()
  ctx.moveTo(heartX, notch)
  ctx.bezierCurveTo(heartX - heartR * 0.5, top, heartX - heartR, top, heartX - heartR, notch)
  ctx.bezierCurveTo(heartX - heartR, s.y(centreZm - tallM * 0.12), heartX - heartR * 0.4,
    s.y(centreZm - tallM * 0.30), heartX, point)
  ctx.bezierCurveTo(heartX + heartR * 0.4, s.y(centreZm - tallM * 0.30), heartX + heartR,
    s.y(centreZm - tallM * 0.12), heartX + heartR, notch)
  ctx.bezierCurveTo(heartX + heartR, top, heartX + heartR * 0.5, top, heartX, notch)
  ctx.closePath()
  ctx.fillStyle = NY_HEART_RED
  ctx.fill()
}

/**
 * An amber side-marker reflector, sitting on the belt the way the real ones do.
 *
 * Handed for the same reason the mask is: the drawing's markers are not symmetric about the
 * bus's centre, so drawn through `s.x` the pair swaps ends between the two flanks — one
 * side's forward marker lands where the other side's rear one is. A reflector is small
 * enough that nobody would catch it, which is exactly why it goes through `flankX` too.
 */
function sideMarker(
  ctx: CanvasRenderingContext2D, s: Scale, w: number, flank: LiveryFlank,
  xm: number, centreZm: number,
) {
  const wM = 0.16, hM = 0.06
  const at = flankX(s, w, flank)
  ctx.fillStyle = '#E8A33D'
  ctx.fillRect(
    Math.min(at(xm - wM / 2), at(xm + wM / 2)), s.y(centreZm + hM / 2),
    s.dx(wM), s.dy(hM),
  )
}

export const LIVERIES = {
  /**
   * No livery. The panel keeps the material the exporter gave it, which is by construction
   * the surrounding body's own — bare stainless on the R160, the standard white on the
   * bus — so this is not "unpainted", it is exactly how the vehicles have always drawn.
   * `null` is what tells the renderer to hand deck the plain model URL and skip the whole
   * patched-glTF path, so the default costs nothing at all.
   *
   * ⚠️ **Offered to the TRAINS only** (`AS_BUILT_OFFERED`), and for the same reason `mta`'s
   * bus half was redrawn from photographs: an R160 genuinely runs unpainted, so as-built is
   * a scheme a rider sees on the platform — but **an MTA bus IS its livery**, and there is
   * no white Xcelsior on any street in the city. The bus's white is the exporter's undressed
   * body, which is a useful thing for the renderer to be able to ask for (the benchmark fleet
   * still does, by id) and not a paint anyone should be offered. It stays `null` on both
   * because the RENDERER's answer for the bus is unchanged; it is the picker that dropped it.
   */
  default: {
    label: 'As-built',
    note: 'Bare stainless, exactly as the R160 runs',
    subway: null,
    bus: null,
  },

  /**
   * Today's paint — and on the bus, the real thing rather than an invention.
   *
   * It used to be one idea drawn twice: a blue-and-gold sine wave down a white body, on both
   * vehicles. The bus half of that was wrong, and wrong in the way that matters most, because
   * **an MTA bus IS its livery** — this is the one vehicle on the map whose paint a rider
   * actually reads. The reference photographs in `mta-assets/reference/BUS-XCEL-40/` show a
   * deep blue body with a straight yellow tail, black glass, and a three-colour wrap — blue,
   * agency gold and cyan, no white — flowing the full length of the flank. That is what the
   * bus wears here now.
   *
   * ⚠️ The two vehicles are DIFFERENT DESIGNS under one theme, and deliberately so: a bus's
   * paint scheme on an R160 would be an invention twice over, since the trains are unpainted
   * stainless in reality. The train wears a band on bare steel — see `subway` below, and the
   * `── The R160's blue band ──` block for the whole drawing.
   */
  mta: {
    label: 'MTA blue',
    note: 'Blue over stainless on the trains, today’s paint on the buses',
    /**
     * The train's own design: bare stainless below the belt moulding, MTA blue from there to
     * the roof, and the cab car's gold-and-white diagonal on the pillar behind the operator's
     * window. Drawn from the user's platform photograph of car 8540 and laid out against the
     * mesh's own features — `── The R160's blue band ──` is where every figure comes from.
     *
     * ⚠️ **The two bodies get different artwork**, which is what `body` is doing in the
     * signature. A B car is band end to end, square at both corners. An A car has a cab in
     * the way, so its band stops amidships in a RAKED edge on the pillar between the second
     * door and the middle window, and its cab pillar carries the same rake again as a
     * three-stripe flash. Because the band is then anchored to one end of the car it has to
     * be handed, which a B car's never would have been.
     *
     * Nothing is a fraction of the sheet: one 1024 × 128 island serves both bodies, but the
     * band's edges are the moulding and the car's own centre, and the diagonal is pinned
     * inside a pillar measured in inches. Written as fractions they would drift the moment
     * either panel moved.
     */
    subway: {
      // The doors, so eight leaves per car do not cut the band into nine pieces. Same
      // reasoning as `classic` below; it is the whole point of the surface list. The band
      // does cross them: the leaves reach z 3.063 against the band's floor at 2.007.
      surfaces: ['sidewall', 'doors'],
      draw: (ctx, w, h, flank, body) => {
        const s = panelScale(w, h, body)
        ctx.fillStyle = STAINLESS
        ctx.fillRect(0, 0, w, h)
        // The B car's band runs corner to corner, square at both ends. The A car's stops on
        // the mid-car pillar in a raked edge — NOT at x = 0, which is 0.256 m inside the
        // middle window, where an edge is drawn onto a surface this livery does not own and
        // is simply not there. See `R160_A_MID_PILLAR_X`.
        const [edgeTop, edgeBot] =
          body === 'r160-a' ? r160BandEdgeX() : [s.x0, s.x0]
        r160BlueBand(ctx, s, w, flank, edgeTop, edgeBot)
        if (body === 'r160-a') r160CabStripes(ctx, s, w, h, flank)
      },
      // Stainless is a metal; painted vinyl over it is not, and one material has to be both.
      // The split leans dielectric because the BAND is the livery: at the panel's authored
      // 0.95 a metal has no diffuse term and #0039A6 renders as a dark mirror rather than as
      // blue. The cost is that the unpainted majority of the flank reads a shade flatter than
      // the R160_Stainless body it abuts, which at map scale nobody sees and a black train
      // very much is.
      finish: { metallic: 0.4, roughness: 0.38 },
      // L2 is one merged shell with no UVs, so the band has to survive as a single value —
      // and a car that reverted to plain steel at distance would look like the livery had
      // failed to load. Blue enough to be recognisably this scheme, light enough to still
      // read as a stainless car wearing it.
      farColor: [0.34, 0.45, 0.7],
    },
    /**
     * The bus, from the photographs. What each reference fixes, and what none of them does:
     *
     *  • **Blue body.** `XE40_photo_mta_front_b48.png` samples #003090 in shade and
     *    `..._notinservice.png` #0050D0 in full sun, either side of the agency's own #0039A6 —
     *    so the published colour is quoted rather than a pixel.
     *  • **Straight yellow tail** (`XE40_photo_mta_rear_b45.png`): the whole rear face, from
     *    the bumper up to the roof shoulder, with the blue coming over the roof and stopping
     *    there. `drawRear` paints it, and the surface it paints exists *because* of it — see
     *    `assign_rear_cap` in `export_bus.py`.
     *  • **The wrap** — a cyan pinstripe fan, a gold-and-cyan sweep, a raked gold stripe fan
     *    at the tail and a cyan shoulder wedge under a pale gold arc, running the whole length
     *    of the flank. See the `── The bus wrap ──` block for what each is drawn from, and for
     *    what the earlier three-arc swirl got wrong.
     *  • **Black glass — but claimed.** Both flank photographs show plain dark tint, so the
     *    glazing ends up the colour it always was. `windows` is claimed anyway, for one
     *    reason: the flag is a decal ON the rear window, and a surface this livery does not
     *    own is a surface it cannot draw on. `busGlazing` pays for that by painting every pane
     *    back. This is still a painted bus, not a perforated-vinyl wrap.
     *  • **Wrapped doors.** `surfaces` claims them, and the door-side photograph is why: the
     *    gold sweep runs straight across both leaves rather than breaking at the doorway. It
     *    is the leaves that are painted, not the glass in them.
     *  • **The flank's four decals**, all from the photographs: the flag on the rear window,
     *    "I ♥ NY" on the band above the third bay from the tail, the agency lock-up under the
     *    driver's window, and four amber side markers straddling the wheel arches.
     *
     * ⚠️ **The lock-up is the one marking that is NOT on both flanks.** The offside carries it
     * low, under the driver's window; the door side carries it high, on the band above the
     * windows — because the station under the driver's window is the front DOOR over there.
     * Only the offside half is drawn: the door side's would land in the middle of the band the
     * wrap keeps clear for it, and nothing in these photographs fixes where along it.
     *
     * What the photographs still do NOT settle: how far the tail's yellow wraps onto the
     * flank, called out where it is drawn. The earlier note here — whether the upper sidewall
     * carries a lighter blue band toward the rear — is answered: it does not. What the night
     * shot hinted at is the shoulder wedge at the FRONT, and it is cyan.
     */
    bus: {
      // Doors, glazing and both caps. The caps are not decoration: unclaimed, they keep the
      // blockout grey their own materials were cut with, so a blue bus would have a grey nose
      // and a grey tail — and the tail is the whole point of this livery.
      //
      // ⚠️ `windows` is claimed for ONE decal — the flag on the rear bay — and it is not free:
      // it hands the wrap every aperture on the vehicle, so `busGlazing` has to paint them all
      // back. This is still a painted bus and not a perforated-vinyl wrap; the glass ends up
      // exactly the colour it was, and the only thing that changes is that a decal can now
      // land on it. See `busGlazing`.
      surfaces: ['sidewall', 'doors', 'windows', 'front', 'rear'],
      draw: (ctx, w, h, flank) => {
        const s = panelScale(w, h, 'xcel40')
        const at = flankX(s, w, flank)

        ctx.fillStyle = MTA_BLUE
        ctx.fillRect(0, 0, w, h)

        // The tail's yellow, wrapping off the rear cap onto the flank around the corner.
        // ⚠️ The 0.35 m is the one figure here chosen by eye rather than measured: the rear
        // photograph shows the yellow turning the corner with blue behind it, but not how far
        // it reaches. It only has to carry the yellow far enough forward that the cap does not
        // end in a seam — the cap's own faces already reach x = -5.893.
        ctx.beginPath()
        ctx.moveTo(at(s.x0), 0)
        ctx.lineTo(at(s.x0 + 0.35), 0)
        ctx.quadraticCurveTo(at(s.x0 + 0.2), h * 0.5, at(s.x0 + 0.41), h)
        ctx.lineTo(at(s.x0), h)
        ctx.closePath()
        ctx.fillStyle = MTA_YELLOW
        ctx.fill()

        // The wrap, front to rear. Order matters where they meet: the pinstripe fan's raked
        // rear edge runs under the lower sweep, and the rear stripe fan crosses the sweep's
        // tail end — which is what both photographs show, the stripes passing over the gold.
        busPinstripeFan(ctx, s, w, flank)
        busLowerSweep(ctx, s, w, flank)
        busRearStripeFan(ctx, s, w, flank)
        busShoulderSweep(ctx, s, w, flank)

        // The body joint under the glazing, where the real panel has its rub rail. Without it
        // the metre of blue below the windows is one flat field, which at distance — where the
        // glazing itself is two pixels — is the whole side of the bus.
        //
        // ⚠️ Drawn AFTER the wrap, not before it as it used to be. The joint is a seam in the
        // panel and the vinyl goes over it, so the photographs show it crossing the gold sweep
        // rather than stopping at it; under the artwork it would simply be buried, since the
        // wrap now covers the length of the flank instead of one patch above the windows.
        ctx.fillStyle = 'rgba(0,0,0,0.16)'
        ctx.fillRect(0, s.y(BUS_SILL_Z), w, s.dy(0.05))

        // The glazing, painted back to glass now the wrap has run over it. Everything below
        // this line is a decal, and the two that sit on glass need it already dark.
        busGlazing(ctx, s, w, flank)

        // The flag, on the REAR WINDOW where the real one is. It used to sit on the wall below
        // that window, because glazing was not this livery's surface and a decal drawn on it
        // would simply not have been there; `windows` is claimed now, so it can go where it
        // belongs.
        //
        // Centred in the measured rear bay rather than placed at a number, so it cannot drift
        // off the glass if the pane is ever re-measured. 0.30 m tall makes it 0.57 m wide
        // against the bay's 1.36 — the proportion the door-side photograph shows. Its base
        // clears the RAKED sill underneath: at the flag's rear edge that sill is 1.69 m.
        const flagTallM = 0.3
        const [bayRear, bayNose] = BUS_REAR_BAY_X
        flagDecal(
          ctx, s, w, flank,
          (bayRear + bayNose) / 2 + (flagTallM * FLAG_RATIO) / 2, 1.72, flagTallM,
        )

        // "I ♥ NY", in the strip of wall above the window header — above the third bay from
        // the tail on the offside, which is the same station as the rear door on the door
        // side, so one body x serves both flanks.
        iLoveNyDecal(ctx, s, w, flank, -0.97, 2.63, 0.19)

        // The agency lock-up under the DRIVER's window, where the offside photograph has it,
        // sitting on the pinstripe fan.
        //
        // ⚠️ Offside only, and not as a stylistic choice: 4.87–5.85 is the driver's window on
        // this flank and the FRONT DOOR on the other, so the same call on the right would put
        // the roundel on a door leaf. The door side carries its lock-up up on the band above
        // the windows instead — see the note in the block comment above.
        //
        // It sits at 0.88 rather than level with the markers below, which is where the
        // photograph has it and is also the only height that works: the lock-up runs back to
        // 4.74 and the forward marker is at 4.70, so at a shared height the amber block lands
        // on the last letter of the wordmark.
        if (flank === 'left') mtaWordmark(ctx, s, w, flank, 5.85, 0.88, 0.0815, 0.1)

        // Amber side markers, at the four stations measured off the reference elevation for
        // this same body — two straddling each wheel arch, since a marker's job is to show an
        // approaching driver where the axles are. They are LAMPS, not paint: they do not move
        // when the livery changes, which is why these are the classic scheme's figures.
        for (const xm of [4.7, 3.7, -2.75, -3.74]) sideMarker(ctx, s, w, flank, xm, 1.0775)
      },
      /**
       * The nose: plain blue. The windscreen, headlights, trim and destination sign are their
       * own materials and mask most of this rectangle, which is why a flat fill is the whole
       * drawing — and why the surface still has to be claimed, since the cap would otherwise
       * keep the blockout grey it was cut with.
       */
      drawFront: (ctx, w, h) => {
        ctx.fillStyle = MTA_BLUE
        ctx.fillRect(0, 0, w, h)
      },
      /**
       * The tail: straight yellow, corner to corner, which is exactly what the rear
       * photograph shows. The tail lamps, the bumper and the trim keep their own materials, so
       * they punch through it rather than needing to be drawn.
       */
      drawRear: (ctx, w, h) => {
        ctx.fillStyle = MTA_YELLOW
        ctx.fillRect(0, 0, w, h)
      },
      // Painted vinyl over steel: no metal, and glossier than the blockout's 0.55 because a
      // bus in service is a waxed surface.
      finish: { metallic: 0.0, roughness: 0.38 },
      // The roof, and it is the surface this map shows most — see `shellColor`. Blue in every
      // reference photo, including over the tail, which is where the yellow stops.
      shellColor: linearFromHex(MTA_BLUE),
      // ⚠️ NOT `linearFromHex(MTA_BLUE)`, unlike `shellColor` two lines up. The far tier is
      // drawn unlit, so this factor is the fragment's colour rather than an input to the lit
      // equation — it is written as the colour it should LOOK, the sRGB reading of MTA blue,
      // the same way every other `farColor` in this table was picked. Run through the linear
      // converter it would arrive at 0.000, 0.041, 0.381 and draw a black bus.
      farColor: [0.0, 0.22, 0.65],
    },
  },

  /**
   * The classic scheme: white body, a dark band around the windows, MTA blue belt under
   * them, and the flag. **A bus livery, and only a bus livery** — it is the paint the bus
   * fleet wore before the wavy blue-and-gold sweep, the one most riders still picture, and
   * a scheme no subway car has ever worn (see `subway` below).
   *
   * It is drawn from a reference elevation of an MTA XE40 (side, rear and front), and the
   * surfaces it claims are that drawing's answer to the coverage question:
   *
   *  • **`front`, because the belt crosses the nose.** It is not a flank marking that
   *    happens to reach the corner — the drawing's front view has the band running the
   *    width of the cap with the roundel centred in it, which is why the bus grew a front
   *    cap surface at all. `drawFront` paints it, on the cap's own elevation.
   *  • **`rear`, because the tail is WHITE** — the drawing's rear is plain white carrying
   *    the agency mark, with no band across it. That is not free: the cap's authored colour
   *    is the blockout grey, and a liveried bus drops the route tint that used to hide it,
   *    so leaving it unclaimed gives a white bus a grey backside.
   *  • **`doors`, so the belt is not sliced.** A door leaf is its own material at near
   *    black; unclaimed, three of them cut the band into four pieces.
   *  • **`windows`, because BLACK GLASS is half of this livery.** This is the one place the
   *    surface rules and the look disagree: the standing advice is that a painted scheme
   *    leaves the glazing alone, and unclaimed glazing here is not black — it is a smooth
   *    dielectric at roughness 0.10 under the map's image-based lighting, so it mirrors the
   *    sky and reads as light silver. On a white body that erases the window band entirely.
   *    The drawing's windows are near-black, so the mask paints them: the glazing shares
   *    the sidewall's unwrap, and `draw` already covers 1.34–2.36 in `WINDOW_MASK`. The
   *    front WINDSCREEN is a different material and stays real glass, which is what keeps
   *    the bus reading as a bus.
   */
  classic: {
    label: 'Classic',
    note: 'White body, dark window band, blue belt and the flag',
    /**
     * ⚠️ **Bus-exclusive, and this null is the whole point.** There is no such thing as a
     * classic MTA subway livery: the white body, dark window band and blue belt are a BUS
     * scheme, and the trains that ran beside those buses were — and are — unpainted
     * stainless. The R160 is not missing this paint, it never had it.
     *
     * It used to be drawn anyway, with the three bands re-measured against the car's own
     * glazing so they at least landed on the right features. That was a well-fitted
     * invention: an agency scheme this fleet never wore, offered to riders in the same
     * list as the two real ones. Being `null` here takes the row out of the subway picker
     * altogether rather than merely leaving the car as-built — a rider is never asked to
     * choose a thing that does not exist.
     *
     * The train's agency option is `mta`, whose subway half is honest about what it is: a
     * band swept along a stainless car, which is the closest thing to a livery a car with
     * no painted body can wear.
     */
    subway: null,
    /**
     * The bus — the body this scheme was drawn for, and the one the reference elevation
     * is of. Every figure below is read off that drawing at the 79.8 px/m it calibrates to
     * against this model's own panel (see `CLASSIC_REFERENCE`), not estimated from it:
     *
     *  • the window band is `BUS_MASK_Z`, which is the glass PLUS its black frame — wider
     *    than the glazing on purpose, and the difference between one ribbon and a row of
     *    painted panes with white pillars left between them;
     *  • the belt is `BUS_BELT_Z`, thinner than it was and no longer touching the band
     *    above it. The drawing leaves ~5 cm of white between the two, and that sliver is
     *    what stops the pair reading as a single heavy stripe;
     *  • the mask stops short of both ends, so the corners fore and aft of the glass stay
     *    white as they do on the drawing.
     *
     * The wordmark sits where the drawing puts it — hard against the FRONT, over the front
     * door — which it can now do because `doors` is a claimed surface, so a decal on a
     * leaf is painted rather than lost. It is anchored to the nose per flank; the reason
     * that is not the same as being drawn twice is in `mtaWordmark`.
     *
     * ⚠️ The drawing's own belt carries the fleet number beside the wordmark, and its rear
     * roofline a New York City seal. Neither is here: the number is per-vehicle and one
     * patched model is shared by the whole fleet, and the seal is 0.24 m of multicolour
     * detail that arrives as two grey pixels. The US flag takes the seal's PLACE — that
     * spot, above the band at the tail, is the only decal position the drawing gives the
     * flank, and it is where the flag was moved to from the bare skirt panel it used to
     * sit on.
     */
    bus: {
      surfaces: ['sidewall', 'doors', 'windows', 'front', 'rear'],
      draw: (ctx, w, h, flank) => {
        const s = panelScale(w, h, 'xcel40')
        const [beltLo, beltHi] = BUS_BELT_Z
        classicScheme(ctx, s, w, h, flank, {
          maskZ: BUS_MASK_Z,
          maskX: BUS_MASK_X,
          beltZ: BUS_BELT_Z,
        })
        const beltMid = (beltLo + beltHi) / 2
        // ⚠️ Doors blocked back out FIRST, so nothing below can land on one. The belt has
        // already run across them at this point and is painted out with them.
        if (flank === 'right') busDoorsPlain(ctx, s)
        // The lock-up is 0.163 m of disc and 0.063 m of lettering — the drawing's own
        // sizes, drawn at those sizes rather than enlarged to stay readable. At map zoom
        // the mark is a mark, which is exactly what it is on the street; a wordmark scaled
        // up until the words could be read would be the one wrong-sized thing on an
        // otherwise measured bus.
        //
        // ⚠️ It is NOT at the drawing's own x. The reference puts it hard against the nose
        // at 4.83–5.90 — which on the real bus is the body panel below the front door, and
        // on THIS body is the front door leaf itself (4.75–5.89), because the model's leaf
        // runs a metre lower than the real one. Drawn there it is a decal on a door, which
        // is not something a bus wears. So it sits in the one stretch of belt this body
        // leaves clear on both flanks: aft of the front wheel arch (3.54) and forward of
        // the centre door (−0.54). Put it back at 5.9 only if the leaf is ever shortened.
        mtaWordmark(ctx, s, w, flank, 3.3, beltMid, 0.0815, 0.1)
        // Amber markers, on the belt where the drawing has them — FOUR, in two pairs, each
        // pair straddling a wheel arch. That is not decoration: a side marker's job is to
        // show an approaching driver where the axles are, so they go either side of the
        // tyre rather than being spaced along the body. Measured off the reference's own
        // amber runs (px 166–170, 240–248, 733–738, 808–814 at 79.8 px/m), which bracket
        // the front tyre at 3.61–4.79 and the rear at −2.68–−3.73.
        for (const xm of [4.7, 3.7, -2.75, -3.74]) sideMarker(ctx, s, w, flank, xm, beltMid)
        // The flag, in the strip of continuous wall between the window band's top (2.41)
        // and the panel's own (2.947), at the tail. That strip is where the reference
        // carries its one flank emblem; below the belt — where this used to be — the
        // drawing's skirt is bare white for the length of the bus. Handed, so the union
        // leads on both flanks: see `flagDecal`.
        flagDecal(ctx, s, w, flank, -4.99, 2.52, 0.26)
      },
      /**
       * The nose: white, with the belt carried across it at the SAME height it runs along
       * the flank, and the roundel centred in the band as the drawing has it. That shared
       * height is what makes it one band rather than two stripes, and `BUS_BELT_Z` is the
       * single constant both surfaces read so it cannot drift at the corner.
       *
       * ⚠️ The band's lowest ~12 cm is behind the headlights, which occupy 0.808–1.080 on
       * this cap. The reference's own front view clears them, but its bus carries its
       * lamps lower than this model does — and since they are their own material and draw
       * over the paint, the band butts against them instead of floating above. Raising the
       * belt to clear them would either thin it below the measured 0.245 m or break the
       * seam with the flank, and both are worse than a band that stops at a headlight.
       */
      drawFront: (ctx, w, h) => {
        const c = capScale(w, h, BUS_CAPS.front)
        const [beltLo, beltHi] = BUS_BELT_Z
        ctx.fillStyle = BUS_WHITE
        ctx.fillRect(0, 0, w, h)
        ctx.fillStyle = MTA_BLUE
        ctx.fillRect(0, c.y(beltHi), w, c.dy(beltHi - beltLo))
        capRoundel(ctx, c, 0, (beltLo + beltHi) / 2, (beltHi - beltLo) * 0.34)
      },
      /**
       * The tail: white, with the full agency lock-up — blue roundel, white letters in it,
       * "New York City Bus" in blue beside it — and no band, the reference's rear view
       * carrying the mark on plain white with the belt stopping at the corner.
       *
       * ⚠️ **Drawn MIRRORED, and it has to be.** This used to be the lone disc, because a
       * cap's u runs from the bus's own right and that is the far side for anyone standing
       * BEHIND the vehicle, so lettering arrived back-to-front while a disc read the same
       * either way. That is a reason to flip the canvas, not a reason to drop half the
       * mark: `capScale`'s `mirror` measures the cap from its other edge and the flip
       * cancels it for position, while the glyphs stay flipped and so read correctly
       * through the unwrap's own mirror. Everything below is therefore authored exactly as
       * it is SEEN from behind the bus.
       *
       * It sits off-centre, toward the bus's left, which is where the drawing has it: the
       * rear's centre line is spoken for by the licence plate and the hitch cover, and
       * those are the model's own materials rather than this livery's. The fleet number
       * that follows the wordmark on the real tail is out for the usual reason — one
       * patched model, the whole fleet.
       */
      drawRear: (ctx, w, h) => {
        ctx.fillStyle = BUS_WHITE
        ctx.fillRect(0, 0, w, h)

        ctx.save()
        ctx.translate(w, 0)
        ctx.scale(-1, 1)
        const c = capScale(w, h, BUS_CAPS.rear, true)
        // Measured off the rear view: the disc is 0.174 m across, centred 0.50 m to the
        // bus's left of the centre line and 2.29 m up — under the destination sign row
        // and above the tail-lamp clusters, both of which are their own materials.
        const zM = 2.29
        const rM = 0.087
        capRoundel(ctx, c, 0.5, zM, rM, { fill: MTA_BLUE, ink: BUS_WHITE })

        const stretch = c.dx(1) / c.dy(1)
        ctx.save()
        ctx.textBaseline = 'middle'
        ctx.textAlign = 'left'
        ctx.scale(stretch, 1)
        ctx.font = canvasBulletFont(c.dy(0.077))
        ctx.fillStyle = MTA_BLUE
        ctx.fillText(NYC_BUS, (c.x(0.5) + c.dx(rM * 1.5)) / stretch, c.y(zM))
        ctx.restore()
        ctx.restore()
      },
      finish: { metallic: 0.0, roughness: 0.42 },
      // The roof, in the body's own white — quoted from the same hex the flank is filled
      // with, so the two cannot drift apart along the roof line. An MTA bus's roof IS white,
      // and on a map that looks straight down it is most of what a rider ever sees of one.
      shellColor: linearFromHex(BUS_WHITE),
      // Cooler and a shade darker than the as-built bus's own far shell (0.46, 0.49,
      // 0.52): same white, but with a heavier window band and a blue belt across it.
      farColor: [0.45, 0.48, 0.54],
    },
  },

  /**
   * A full-body advertising wrap — the thing a livery system exists for. Deliberately loud
   * and deliberately NOT an MTA colour, so it is obvious at a glance that the wrap is being
   * applied and to which surfaces; a subtle first livery would be indistinguishable from a
   * bug in the unwrap.
   */
  wrap: {
    label: 'Ad wrap',
    note: 'Full-body advertising vinyl',
    subway: {
      draw: (ctx, w, h) => {
        const g = ctx.createLinearGradient(0, 0, w, 0)
        g.addColorStop(0, '#7B1FA2')
        g.addColorStop(0.5, '#E91E63')
        g.addColorStop(1, '#FF9800')
        ctx.fillStyle = g
        ctx.fillRect(0, 0, w, h)
        sweep(ctx, w, h, 'rgba(255,255,255,0.22)', {
          centre: 0.3, thickness: 0.18, amplitude: 0.2, cycles: 2.2,
        })
      },
      finish: { metallic: 0.0, roughness: 0.35 },
      farColor: [0.62, 0.22, 0.42],
    },
    bus: {
      draw: (ctx, w, h) => {
        const g = ctx.createLinearGradient(0, 0, w, 0)
        g.addColorStop(0, '#00897B')
        g.addColorStop(0.55, '#43A047')
        g.addColorStop(1, '#C0CA33')
        ctx.fillStyle = g
        ctx.fillRect(0, 0, w, h)
        sweep(ctx, w, h, 'rgba(0,0,0,0.18)', {
          centre: 0.42, thickness: 0.22, amplitude: 0.22, cycles: 1.8, phase: 1.1,
        })
      },
      finish: { metallic: 0.0, roughness: 0.35 },
      // The roof keeps the agency's own paint, because that is what a real wrap does: the
      // vinyl covers the flanks and stops at the shoulder. Set anyway rather than left to the
      // blockout grey — see `shellColor`; the roof is what this map looks straight down at.
      shellColor: linearFromHex(BUS_WHITE),
      farColor: [0.24, 0.55, 0.34],
    },
  },

  /**
   * The Minions. Was a `settings.minionTheme` boolean that multiplied the whole model
   * yellow and gave the train no face at all; it is a livery now, so the paint stops at
   * the sidewall the way every other one does and the two vehicles can wear DIFFERENT
   * designs instead of one tint.
   *
   * They are deliberately not the same crowd on both bodies, because the bodies are not
   * the same shape. See each `draw`.
   */
  minion: {
    label: 'Minions',
    note: 'A crew of them along both flanks, goggles and all',
    /**
     * The train: eight smaller minions per car, and — the thing that makes this a TRAIN
     * livery rather than the bus one scaled — one goggle strap running the full 17.7 m at
     * their eye height instead of each wearing its own. A consist is eight cars end to
     * end, so the strap chains into a single band the length of the whole train, which is
     * how a real subway livery's stripe behaves and is the one thing a bus cannot do.
     */
    subway: {
      // `body` only to size the panel: the A and B cars are the same crowd. A cartoon does
      // not care that one of them has a cab, which is the case `draw`'s body argument is
      // explicitly allowed to ignore.
      draw: (ctx, w, h, _flank, body) => {
        const s = panelScale(w, h, body)
        minionField(ctx, s, w, h)

        // The skirt they stand on, and the continuous strap at eye height.
        const feet = s.z0 + 0.30, tall = 1.55
        const eyeY = feet + 0.70 * tall
        ctx.fillStyle = MINION.denimDark
        ctx.fillRect(0, s.y(s.z0 + 0.38), w, s.dy(0.38))
        ctx.fillStyle = MINION.strap
        ctx.fillRect(0, s.y(eyeY + 0.05 * tall), w, s.dy(0.10 * tall))

        const COUNT = 8
        const pitch = (s.x1 - s.x0) / COUNT
        for (let i = 0; i < COUNT; i++) {
          minion(ctx, s, s.x0 + pitch * (i + 0.5), feet, tall, {
            eyes: i % 2 === 0 ? 2 : 1,
            sprigs: i % 3 === 0 ? 2 : 0,
            // Their own straps would double up over the car-length band above.
            strap: false,
          })
        }
      },
      // The doors join the flank, and nothing else does. An R160's cab is glazing and a
      // black front plate with no skin to paint, and its side windows are the one thing
      // telling a rider it is a train rather than a yellow tube — perforated vinyl over
      // them is a bus trick and reads wrong on a subway car.
      surfaces: ['sidewall', 'doors'],
      // Painted vinyl over what the exporter left at stainless 0.95. See the `finish` note
      // on LiveryPaint: leave it metallic and yellow renders as a dark gold mirror.
      finish: { metallic: 0.0, roughness: 0.42 },
      // Mostly yellow, pulled down by the denim skirt and the strap band across it.
      farColor: [0.82, 0.58, 0.08],
    },
    /**
     * The bus: four of them, half again as tall, each with its own goggle strap. The
     * Xcelsior's panel is a third shorter than the R160's but nearly half a metre taller,
     * so the train's crowded row would leave a metre of empty yellow over their heads —
     * fewer and bigger fills the flank the body actually has.
     */
    bus: {
      draw: (ctx, w, h, flank) => {
        const s = panelScale(w, h, 'xcel40')
        minionField(ctx, s, w, h)

        const feet = s.z0 + 0.26, tall = 1.92
        ctx.fillStyle = MINION.denimDark
        ctx.fillRect(0, s.y(s.z0 + 0.30), w, s.dy(0.30))

        const COUNT = 4
        const pitch = (s.x1 - s.x0) / COUNT
        for (let i = 0; i < COUNT; i++) {
          minion(ctx, s, s.x0 + pitch * (i + 0.5), feet, tall, {
            eyes: i % 2 === 0 ? 1 : 2,
            sprigs: i % 2 === 0 ? 3 : 1,
          })
        }

        // The front doors' glazing, handed back to the glass — LAST, over the crowd. The
        // wrap claims `windows`, which is what an ad wrap is over the passenger bays and
        // wrong at the doorway riders board through: a wrapped front door reads as a bus
        // whose operator cannot see the kerb. `windows` is one material, so the exception is
        // painted rather than declared. Door side only — see `BUS_FRONT_DOOR_PANES`.
        if (flank === 'right') busFrontDoorGlass(ctx, s)
      },
      /**
       * Everything the wrap can reach. A bus is the vehicle a full wrap is actually sold
       * on, so this is the livery that takes all of it: doors and glazing as perforated
       * vinyl, and both caps. The roof is left out — it is the one surface a rider on the
       * street never sees, and the map's camera barely does either.
       *
       * ⚠️ `windows` is the whole flank's glazing, and the FRONT DOOR's panes are the one
       * part of it this livery does not want. A surface cannot be claimed in part, so `draw`
       * paints those two panes back to glass; the declaration here is the wider of the two
       * statements and the artwork is the narrower.
       */
      surfaces: ['sidewall', 'doors', 'windows', 'front', 'rear'],
      /**
       * The face — and the reason the six `MINION_BUS_*` overlay meshes are gone.
       *
       * ⚠️ Drawn as if the whole nose were skin, because the windscreen, headlights and
       * trim are their own materials and mask it. That is what makes the joke land: the
       * goggle rims are painted around the windscreen, so the WINDSCREEN reads as the
       * lens. Painting a lens as well would put a picture of glass over real glass.
       */
      drawFront: (ctx, w, h) => {
        // The windscreen spans z 0.803–2.426 of the cap's own 0.122–3.048, so its centre
        // sits at 0.51 of the canvas measured up from the bottom. Everything keys off that
        // rather than off the canvas's middle, which is 6 cm lower and looks it.
        const eye = 0.49 * h
        ctx.fillStyle = MINION.yellow
        ctx.fillRect(0, 0, w, h)
        ctx.fillStyle = MINION.yellowShade
        ctx.fillRect(0, 0, w, 0.05 * h)

        ctx.fillStyle = MINION.strap
        ctx.fillRect(0, eye - 0.10 * h, w, 0.20 * h)

        // Two rims, sized to sit just outside the windscreen so the band of skin around
        // the glass carries them. Stroked, not filled — a filled disc would paint over
        // nothing (the glass is another material) and cost the rim its thickness.
        ctx.lineWidth = 0.055 * w
        ctx.strokeStyle = MINION.goggle
        for (const cx of [0.29 * w, 0.71 * w]) {
          ctx.beginPath()
          ctx.arc(cx, eye, 0.235 * w, 0, Math.PI * 2)
          ctx.stroke()
        }
        ctx.lineWidth = 0.018 * w
        ctx.strokeStyle = MINION.goggleDark
        for (const cx of [0.29 * w, 0.71 * w]) {
          ctx.beginPath()
          ctx.arc(cx, eye, 0.262 * w, 0, Math.PI * 2)
          ctx.stroke()
        }

        // The smile goes on the lower fascia, below the windscreen, which is solid skin.
        ctx.beginPath()
        ctx.strokeStyle = MINION.mouth
        ctx.lineWidth = 0.03 * w
        ctx.lineCap = 'round'
        ctx.moveTo(0.34 * w, 0.13 * h)
        ctx.quadraticCurveTo(0.5 * w, 0.055 * h, 0.66 * w, 0.13 * h)
        ctx.stroke()

        // Sprigs on the crown, above the windscreen header.
        ctx.strokeStyle = MINION.hair
        ctx.lineWidth = 0.022 * w
        for (const cx of [0.42 * w, 0.5 * w, 0.58 * w]) {
          ctx.beginPath()
          ctx.moveTo(cx, 0.995 * h)
          ctx.quadraticCurveTo(cx + 0.03 * w, 0.93 * h, cx - 0.015 * w, 0.89 * h)
          ctx.stroke()
        }
      },
      /** The tail: overalls, no face. A minion seen from behind is a minion in dungarees. */
      drawRear: (ctx, w, h) => {
        ctx.fillStyle = MINION.yellow
        ctx.fillRect(0, 0, w, h)
        ctx.fillStyle = MINION.yellowShade
        ctx.fillRect(0, 0, w, 0.05 * h)
        ctx.fillStyle = MINION.denim
        ctx.fillRect(0, 0.55 * h, w, 0.45 * h)
        // Straps over the shoulders, meeting the bib.
        ctx.strokeStyle = MINION.denim
        ctx.lineWidth = 0.06 * w
        for (const dir of [-1, 1]) {
          ctx.beginPath()
          ctx.moveTo(0.5 * w + dir * 0.17 * w, 0.55 * h)
          ctx.lineTo(0.5 * w + dir * 0.27 * w, 0.14 * h)
          ctx.stroke()
        }
        ctx.fillStyle = MINION.denimDark
        ctx.fillRect(0.38 * w, 0.66 * h, 0.24 * w, 0.02 * h)
      },
      finish: { metallic: 0.0, roughness: 0.45 },
      // The roof, yellow with the rest of the creature. This is the surface that made the
      // "a rider never sees the roof" argument above worth revisiting: a rider does not, but
      // this map's camera has no pitch, so it is the first thing the map sees. A minion bus
      // with a grey lid was the one view where the joke did not land.
      shellColor: linearFromHex(MINION.yellow),
      // More yellow reaches the eye here than on the train: a shallower skirt, no band.
      farColor: [0.88, 0.64, 0.06],
    },
  },
} as const satisfies Record<string, Livery>

export type LiveryId = keyof typeof LIVERIES

/** The two bodies the table dresses, and the two pickers the sheet now carries. */
export type LiveryVehicle = 'subway' | 'bus'

export const LIVERY_VEHICLES: readonly LiveryVehicle[] = ['subway', 'bus']

export const LIVERY_IDS = Object.keys(LIVERIES) as LiveryId[]

/**
 * Which fleets are offered AS-BUILT — the one row `LIVERY_IDS_FOR` cannot read off the
 * table, because `default` is `null` on both bodies for a reason that has nothing to do
 * with who may wear it.
 *
 * Trains only. An R160 is unpainted stainless in service, so as-built is a real scheme a
 * rider can stand next to. A white Xcelsior is not: **an MTA bus IS its livery**, and the
 * bus's `AS_BUILT_FLANK` white is the colour the exporter cut the blockout in, not paint
 * MTA has ever run. Offering it put a vehicle in the picker that does not exist, and made
 * it the fleet's default — see `DEFAULTS.busLivery`, now `mta`.
 *
 * ⚠️ This gates the PICKER, not the renderer. `liveryPaint('default', 'bus')` still returns
 * null and still yields the plain model, which is what the benchmark fleet asks for by id.
 */
const AS_BUILT_OFFERED: readonly LiveryVehicle[] = ['subway']

/** Offered = as-built where that fleet is offered it, or a paint for that body. */
const offersLivery = (vehicle: LiveryVehicle, id: LiveryId): boolean =>
  id === 'default' ? AS_BUILT_OFFERED.includes(vehicle) : LIVERIES[id][vehicle] !== null

/**
 * What each vehicle's picker offers — **two different lists, on purpose.**
 *
 * The fleets do not share a paint history. `classic` is a bus scheme with no subway
 * counterpart, so it appears in the bus list and nowhere else; a future train-only livery
 * goes the other way and needs no new machinery. Selection is therefore per vehicle
 * (`settings.subwayLivery` / `settings.busLivery`), and a theme the two fleets happen to
 * share is a theme that happens to have both halves filled in, not a rule.
 *
 * `default` is in the subway list and not the bus one — see `AS_BUILT_OFFERED`. It is the
 * only id whose membership is not decided by a null check.
 */
export const LIVERY_IDS_FOR: Record<LiveryVehicle, LiveryId[]> = {
  subway: LIVERY_IDS.filter((id) => offersLivery('subway', id)),
  bus: LIVERY_IDS.filter((id) => offersLivery('bus', id)),
}

export function isLiveryId(value: unknown): value is LiveryId {
  return typeof value === 'string' && value in LIVERIES
}

/**
 * The guard persisted settings actually need: an id this VEHICLE can wear.
 *
 * `isLiveryId` alone is no longer enough — `'classic'` is a real id and a real bus livery,
 * and a stored subway setting of `'classic'` is still nonsense. Anything this rejects falls
 * back to that fleet's own `DEFAULTS` entry rather than painting a vehicle a scheme it never
 * wore. It also rejects `'default'` on the bus, which is what carries every rider stored on
 * as-built over to `mta` on next load — see `loadSettings`.
 */
export function isLiveryIdFor(vehicle: LiveryVehicle, value: unknown): value is LiveryId {
  return isLiveryId(value) && LIVERY_IDS_FOR[vehicle].includes(value)
}

/**
 * The paint this livery puts on this vehicle.
 *
 * `null` covers both "as built" and "this body is not offered this livery" — the renderer
 * wants the same thing for either (the plain model URL, no patch, no canvas), so the two
 * are one case here. The difference is a picker's, and `LIVERY_IDS_FOR` is where it lives.
 */
export function liveryPaint(id: LiveryId, vehicle: LiveryVehicle): LiveryPaint | null {
  return LIVERIES[id][vehicle]
}
